/**
 * «Чаты» (`/chats/<id>`): общий мессенджер клиентов — Telegram, MAX, Авито, ВКонтакте в одном
 * списке — и ответ из кабинета. Вид — как Telegram Web: слева чаты, в середине лента и поле
 * ввода, справа сделка диалога; на телефоне — одна колонка. Закрыто правом `client_chats`.
 * Спека `docs/specs/2026-10-02-client-chats-design.md`.
 */
import { useGetIdentity } from "@refinedev/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Bell,
  BellOff,
  Check,
  CheckCheck,
  ClipboardList,
  Clock,
  Download,
  ExternalLink,
  Eye,
  Hash,
  FileText,
  Forward,
  Reply,
  ListChecks,
  LoaderCircle,
  Megaphone,
  Menu,
  MoreHorizontal,
  PanelRight,
  Paperclip,
  Phone,
  PhoneCall,
  PhoneIncoming,
  PhoneMissed,
  PhoneOutgoing,
  ScanSearch,
  Search,
  SendHorizontal,
  Settings,
  Shirt,
  Sparkles,
  StickyNote,
  Store,
  X,
} from "lucide-react";
import {
  type CSSProperties,
  Fragment,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Link, type SetURLSearchParams, useLocation, useNavigate, useParams, useSearchParams } from "react-router";

import { Annotator } from "@/components/Annotator";
import { AccountPlace, Card } from "@/components/chat/AccountOnline";
import { CallBubble } from "@/components/chat/CallBubble";
import { CommentBubble } from "@/components/chat/CommentBubble";
import { LeadBubble } from "@/components/chat/LeadBubble";
import { NoticeBubble } from "@/components/chat/NoticeBubble";
import { TrackBubble } from "@/components/chat/DealTracks";
import { VisitCard } from "@/components/chat/VisitCard";
import { ChatFilters, type FilterKey } from "@/components/chat/ChatFilters";
import { ChatTags } from "@/components/chat/ChatTags";
import { fmtPhone } from "@/components/chat/Contacts";
import { ClientTab, clientQuery, clientTraces } from "@/components/chat/ClientTab";
import { QuickButton } from "@/components/chat/ChatQuick";
import { SalonForwardButton } from "@/components/chat/SalonForward";
import { ComposeModeBar, salonQuery } from "@/components/chat/ComposeMode";
import { type MenuItem, MessageMenu } from "@/components/chat/MessageMenu";
import { TabStrip } from "@/components/chat/TabStrip";
import { type ComposeMode, sendComment, sendSalonQuestion } from "@/lib/compose";
import { ChatAiHistory } from "@/components/chat/ChatAiHistory";
import { ChatAiButton, ChatSuggestCard, useChatAi } from "@/components/chat/ChatSuggest";
import { ChatPodbor } from "@/components/chat/Podbor";
import { CallAttempts } from "@/components/chat/CallAttempts";
import { TemplateButton } from "@/components/chat/ChatTemplates";
import { CallbackBar, LeadsBar, WaitingBar, WaitingList, useWaiting } from "@/components/chat/WaitingBar";
import { DealPanel } from "@/components/chat/DealPanel";
import { GroupAccess } from "@/components/chat/GroupAccess";
import {
  CallButton,
  callText,
  DialCard,
  DialTools,
  FoundByPhone,
  FoundByPhoneButton,
  foundItems,
  phoneItem,
  searchPhone,
  dialName,
  dialObject,
  dialPerson,
  dialSource,
  dialWhat,
  missedText,
  missedTryText,
} from "@/components/chat/Dial";
import { DressCatalog } from "@/components/chat/DressCatalog";
import { IncomingCalls } from "@/components/chat/IncomingCalls";
import { loadSearch } from "@/lib/search";
import { AvitoItem } from "@/components/chat/AvitoItem";
import { DressSearch } from "@/components/chat/DressSearch";
import { NUMBER_MODES, SearchDrop, type SearchMode } from "@/components/chat/SearchDrop";
import { SerialCardDialog } from "@/components/chat/SerialCard";
import { MessageText, OpenPhone, useOpenNumber } from "@/components/chat/MessageText";
import { ChatTrail } from "@/components/chat/ChatTrail";
import { WaError, WaWindowNote, waStage } from "@/components/chat/WaWindow";
import { ForwardDialog } from "@/components/chat/Forward";
import { PickBar, PickHeaderButton } from "@/components/chat/PickBar";
import { NoAccess } from "@/components/NoAccess";
import { QcList, QcPanel, QcTabCount } from "@/components/qc/QcPanel";
import { QcComposeSwitch } from "@/components/qc/RemarkComposer";
import { QcHeaderButton, QcProvider, QcRow, remarkItem, useQc } from "@/components/qc/QcSelection";
import { CHAT_GROUPS_EDIT, CHAT_SUGGEST, CLIENT_CHATS, has, KNOWLEDGE_EDIT, QC_REMARK, useCan } from "@/lib/access";
import { ApiFailure } from "@/lib/api";
import {
  type Channel,
  type Chat,
  type ChatBrief,
  type ChatButton,
  type ChatCall,
  type ChatComment,
  type ChatLead,
  type ChatNotice,
  type ChatVisit,
  type ChatFile,
  type ChatLast,
  type ChatKind,
  type ChatMailingMark,
  type ChatMessage,
  type DialItem,
  type DialScope,
  type FileKind,
  type GroupType,
  type MailingPick,
  type ReplyPlace,
  type TaskPick,
  type ZapisPick,
  type KbqPick,
  type KbqInfo,
  type LeadsPick,
  type LeadsInfo,
  type MovedPick,
  type MovedInfo,
  CHANNELS,
  GROUP_TYPES,
  PLAIN,
  accountName,
  channelOf,
  loadChat,
  loadChatFilters,
  loadChatHistory,
  loadChatNumber,
  type NumberFound,
  loadChatBadges,
  loadChats,
  loadChatTags,
  loadDial,
  loadMyCalls,
  markAnswered,
  muteGroup,
  placeFull,
  refreshChat,
  regionText,
  sendFileToChat,
  sendToChat,
  type StartAccount,
  startChat,
  webAccount,
  webChatUrl,
} from "@/lib/clientChats";
import { type Track, dealTracksKey, loadDealTracks } from "@/lib/dealTracks";
import type { StageKind } from "@/lib/dealStage";
import { SOURCE_COL } from "@/lib/leadSources";
import type { CurrentUser } from "@/lib/providers";

/** Шаг рассылки в подписи выборки (`/chats?mailing=…&step=`); без шага — все отправленные. */
const PICK_STEP: Record<string, string> = {
  failed: "не дошло",
  delivered: "дошло",
  read: "прочитано",
  replied: "ответили",
  opened: "открыли кабинет",
  booked: "записались сами",
  click_max: "нажали MAX",
  click_telegram: "нажали Telegram",
  bot_max: "запустили бота MAX",
  bot_telegram: "запустили бота Telegram",
  recorded: "записаны",
  reserved: "забронировали",
  replied_recorded: "ответили и записаны",
  replied_reserved: "ответили и забронировали",
};
/** Сколько в выборке: отправок больше, чем чатов, когда одному номеру ушло несколько шаблонов. */
const pickCount = (sends: number, chats: number) =>
  sends === chats ? `чатов: ${chats}` : `отправок: ${sends} → чатов: ${chats} (номеру ушло несколько)`;
/** Подпись выборки по записям: людей в «Записях» и чатов; расходятся, когда чат записи не найден. */
const zapisCount = (people: number, chats: number) =>
  people === chats ? `чатов: ${chats}` : `записано: ${people} → чатов: ${chats}`;
/** Подпись выборки переезда: броней (число с экрана рассылки), у скольких нашёлся чат, чатов всего. */
const movedCount = (m: MovedInfo) =>
  [
    `${m.label.endsWith("записанные") ? "записей" : "броней"}: ${m.count}`,
    m.with_chat < m.count ? `с чатом: ${m.with_chat}` : "",
    `чатов: ${m.chats}`,
  ]
    .filter(Boolean)
    .join(" → ");
/**
 * Подпись выборки по «Источникам»: число из таблицы, сколько из них нашли чат, и чатов — их больше,
 * когда у клиента несколько (WhatsApp, MAX, Telegram); список «Чатов» — до `limit` свежих.
 */
const leadsCount = (x: LeadsInfo) => {
  const unit = SOURCE_COL[x.col]?.unit ?? "";
  const chats = x.chats > x.limit ? `чатов: ${x.chats}, в списке ${x.limit} свежих` : `чатов: ${x.chats}`;
  return x.with_chat === x.count ? `${unit}: ${x.count} → ${chats}` : `${unit}: ${x.count}, с чатом ${x.with_chat} → ${chats}`;
};
/** «03.10» из `2026-10-03`. */
const dm = (day: string) => `${day.slice(8, 10)}.${day.slice(5, 7)}`;

/** Градиенты аватаров Telegram: цвет — по id собеседника. */
const AVATAR = [
  ["#ff885e", "#ff516a"],
  ["#ffcd6a", "#ffa85c"],
  ["#82b1ff", "#665fff"],
  ["#a0de7e", "#54cb68"],
  ["#53edd6", "#28c9b7"],
  ["#72d5fd", "#2a9ef1"],
  ["#e0a2f3", "#d669ed"],
] as const;
const MONTHS = "января февраля марта апреля мая июня июля августа сентября октября ноября декабря".split(" ");
const MONTHS_SHORT = "янв фев мар апр мая июн июл авг сен окт ноя дек".split(" ");
/** Год даты, когда он не текущий: переписка и рассылки по клиентам прошлых лет стоят в одной ленте. */
const otherYear = (iso: string) => (iso.slice(0, 4) === todayMsk().slice(0, 4) ? "" : iso.slice(0, 4));
const dayShort = (iso: string) =>
  `${Number(iso.slice(8, 10))} ${MONTHS_SHORT[Number(iso.slice(5, 7)) - 1]}${otherYear(iso) && ` ${otherYear(iso)}`}`;
/** `2026-10-04T12:00:00` → «4 окт 12:00»; полночь — без времени. */
const whenShort = (iso: string) => {
  const t = iso.slice(11, 16);
  return t && t !== "00:00" ? `${dayShort(iso)} ${t}` : dayShort(iso);
};
/** Цвет строки сделки по виду стадии — как в панели; на выбранной строке всё белое. */
const DEAL_COLOR: Record<StageKind, string> = {
  work: "text-[var(--tg-primary)]",
  wait: "text-[#c98a00]",
  think: "text-[var(--tg-text)]",
  refuse: "text-[#e53935]",
  won: "text-[#2e7d32]",
  other: "text-[var(--tg-sec)]",
};
/** Третья строка чата: «Сделка N · стадия · запись 4 окт 12:00» / «обработка …» / «прокат 10–12 окт». */
function dealLine(c: Pick<Chat, "deal" | "lead">): { text: string; kind: StageKind } | null {
  if (c.deal) {
    const d = c.deal;
    let tail = "";
    if (d.stage_code === "4" || d.stage_code === "2") {
      if (d.rent_start) tail = ` · прокат ${dayShort(d.rent_start)}${d.rent_end ? `–${dayShort(d.rent_end)}` : ""}`;
    } else if (d.kind === "wait") tail = d.date ? ` · запись ${whenShort(d.date)}` : " · без даты записи";
    else if (d.kind === "work" || d.kind === "think") tail = d.date ? ` · обработка ${whenShort(d.date)}` : "";
    return { text: `Сделка ${d.id} · ${d.stage}${tail}`, kind: d.kind };
  }
  if (c.lead) {
    const l = c.lead;
    const tail = l.active && l.date ? ` · обработка ${whenShort(l.date)}` : "";
    return { text: `Лид ${l.id} · ${l.status}${tail}`, kind: l.active ? "work" : "other" };
  }
  return null;
}

const hhmm = (iso: string) => iso.slice(11, 16);
/** Время базы — московское; «сегодня» считаем тоже по Москве. */
const todayMsk = () => new Date(Date.now() + 3 * 3600_000).toISOString().slice(0, 10);
/** «Сейчас» в том же виде, что даты сервера: московское время без пояса. */
const localIso = () => new Date(Date.now() + 3 * 3600_000).toISOString().slice(0, 19);
const listWhen = (iso: string | null) =>
  !iso
    ? ""
    : iso.slice(0, 10) === todayMsk()
      ? hhmm(iso)
      : `${iso.slice(8, 10)}.${iso.slice(5, 7)}${otherYear(iso) && `.${otherYear(iso).slice(2)}`}`;
/**
 * Лента чата: сообщения, звонки клиента и комментарии сотрудников из CRM в одной хронологии.
 * Сообщениям оставляем их соседей по переписке (хвостик пузыря и подпись «где общались» считаются
 * по ним), звонок несёт свою дату, комментарии подряд за один день — одна карточка, приход в
 * салон — своя карточка по времени начала примерки (его комментарии — внутри неё, `VisitCard`).
 * `day` — запись первая за свой день среди ВСЕХ записей ленты: по ней рисуем разделитель дня
 * (раньше его считал пузырь по предыдущему сообщению — звонок или комментарий в день без
 * сообщений оказывался под чужой датой).
 */
type FeedItem = (
  | { m: ChatMessage; prev: ChatMessage | undefined; next: ChatMessage | undefined }
  | { call: ChatCall }
  | { comments: [ChatComment, ...ChatComment[]] }
  | { visit: ChatVisit }
  | { notice: ChatNotice }
  | { lead: ChatLead }
  | { track: Track; at: string }
) & { day: boolean };

const feedAt = (it: FeedItem) =>
  "call" in it
    ? it.call.date
    : "comments" in it
      ? it.comments[0].date
      : "visit" in it
        ? it.visit.date
        : "notice" in it
          ? it.notice.date
          : "lead" in it
            ? it.lead.date
          : "track" in it
            ? it.at
            : it.m.date;
const sameDay = (a: string, b: string) => a.slice(0, 10) === b.slice(0, 10);

function merge(
  messages: ChatMessage[],
  calls: ChatCall[],
  comments: ChatComment[],
  visits: ChatVisit[],
  notices: ChatNotice[] = [],
  tracks: Track[] = [],
  leads: ChatLead[] = [],
): FeedItem[] {
  const raw: FeedItem[] = messages.map((m, i, list) => ({ m, prev: list[i - 1], next: list[i + 1], day: false }));
  if (calls.length > 0 || comments.length > 0 || visits.length > 0 || notices.length > 0 || tracks.length > 0 || leads.length > 0) {
    raw.push(
      ...calls.map((call) => ({ call, day: false })),
      ...comments.map((c): FeedItem => ({ comments: [c], day: false })),
      ...visits.map((visit) => ({ visit, day: false })),
      ...notices.map((notice) => ({ notice, day: false })),
      ...leads.map((lead) => ({ lead, day: false })),
      // Трек — по дате создания в CRM («2026-10-20 12:17», MSK) в том же виде, что даты ленты.
      ...tracks.filter((t) => t.created).map((track) => ({ track, at: `${track.created!.replace(" ", "T")}:00`, day: false })),
    );
    raw.sort((a, b) => (feedAt(a) < feedAt(b) ? -1 : feedAt(a) > feedAt(b) ? 1 : 0));
  }
  const out: FeedItem[] = [];
  for (const it of raw) {
    const last = out[out.length - 1];
    if (last && "comments" in last && "comments" in it && sameDay(feedAt(last), feedAt(it))) {
      last.comments.push(...it.comments);
      continue;
    }
    it.day = !last || !sameDay(feedAt(last), feedAt(it));
    out.push(it);
  }
  return out;
}

const dayLabel = (iso: string) =>
  iso.slice(0, 10) === todayMsk()
    ? "Сегодня"
    : `${Number(iso.slice(8, 10))} ${MONTHS[Number(iso.slice(5, 7)) - 1]}${otherYear(iso) && ` ${otherYear(iso)}`}`;
const KIND: Record<FileKind, string> = {
  photo: "Фото",
  video: "Видео",
  animation: "GIF",
  video_note: "Видеосообщение",
  voice: "Голосовое",
  audio: "Аудио",
  sticker: "Стикер",
  document: "Файл",
  product: "Товар",
};
const MB = 1024 * 1024;
const MAX_SEND = 50 * MB; // больше бот не отправит
// WhatsApp (Wazzup) вне окна 24 ч: текст уходит «Универсальным шаблоном 4» — одной строкой до 550
// знаков, файлы не уходят вовсе (`chat/whatsapp.py`, `ШАБЛОН_ЗНАКОВ`).
const WA_TEMPLATE_CHARS = 550;
/** Почему отправка в WhatsApp с закрытым окном 24 ч не пройдёт наверняка; пусто — пройдёт. */
function waClosedProblem(value: string, files: number): string {
  if (files > 0) return "Окно 24 ч WhatsApp закрыто — файлы не отправить, только текст шаблоном";
  // Как сервер: переводы строк и пробелы — одним пробелом; знаки — кодовые точки, как `len` в Python.
  const line = [...value.split(/\s+/).filter(Boolean).join(" ")].length;
  return line > WA_TEMPLATE_CHARS
    ? `Окно 24 ч WhatsApp закрыто — шаблоном уходит до ${WA_TEMPLATE_CHARS} знаков одной строкой, сейчас ${line}`
    : "";
}
const sizeOf = (n: number | null) =>
  n == null ? "" : n >= MB ? `${(n / MB).toFixed(1)} МБ` : `${Math.max(1, Math.round(n / 1024))} КБ`;
/** Строка списка чатов: подпись, а без неё — вид вложения. */
const preview = (l: ChatLast) => (l.kind ? [KIND[l.kind], l.text].filter(Boolean).join(" · ") : l.text || "‹без текста›");
const titleOf = (c: ChatBrief) => c.name || (c.username ? `@${c.username}` : `id ${c.external_id}`);
/** Чат ждёт ответа: последнее сообщение, не считая автоматических, — клиента и отметки «ответили» нет. */
const waitsReply = (c: ChatBrief) => c.waits && !c.answered;
// Каналы и подписи «где общались» живут в `lib/clientChats` (нужны и панели расширения).
export { channelOf };

/**
 * Где мы общались, коротко — каналы зовём как во вкладках: «WA «Wazzup»», «TG-бот»,
 * «MAX «Сева-2»», «Инста МСК», «Авито». Не имя клиента: оно стоит рядом.
 */
function placeOf(c: ChatBrief): string {
  const ch = channelOf(c.channel);
  const name = accountName(c.account);
  if (name) return `${ch.short} ${name}`;
  if (c.context?.web) return `${ch.short} из браузера`;
  return PLAIN[c.channel] === "бот" ? `${ch.short}-бот` : ch.short;
}
/** Шапка чата: канал так же коротко, но с номером аккаунта — «WA «Света» +7 925 605-07-25 · из браузера». */
function placeHead(c: ChatBrief): string {
  const name = accountName(c.account, true);
  if (!name) return placeOf(c);
  return `${channelOf(c.channel).short} ${name}${c.context?.web ? " · из браузера" : ""}`;
}

function Avatar({ chat, size }: { chat: ChatBrief; size: number }) {
  const [from, to] = AVATAR[Math.abs(Number(chat.external_id.replace(/\D/g, "").slice(-6)) || chat.id) % AVATAR.length]!;
  const [broken, setBroken] = useState(false);
  const initials = titleOf(chat)
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w.replace("@", "")[0]?.toUpperCase() ?? "")
    .join("");
  const ch = channelOf(chat.channel);
  return (
    <div className="relative flex-none" style={{ width: size, height: size }}>
      {chat.avatar && !broken ? (
        <img
          src={chat.avatar}
          alt=""
          referrerPolicy="no-referrer"
          onError={() => setBroken(true)}
          className="size-full rounded-full object-cover"
        />
      ) : (
        <div
          className="grid size-full place-items-center rounded-full font-medium text-white"
          style={{ fontSize: size * 0.37, background: `linear-gradient(${from}, ${to})` }}
        >
          {initials}
        </div>
      )}
      <span
        title={ch.label}
        className="absolute -bottom-0.5 -right-0.5 grid place-items-center rounded-full font-semibold text-white ring-2 ring-[var(--tg-surface)]"
        style={{ width: size * 0.36, height: size * 0.36, fontSize: size * 0.2, background: ch.color }}
      >
        {ch.mark}
      </span>
    </div>
  );
}

export function ChatsPage() {
  const { data: user, isPending } = useGetIdentity<CurrentUser>();
  if (isPending) return null;
  return has(user, CLIENT_CHATS) ? (
    <Screen />
  ) : (
    <div className="flex h-dvh flex-col">
      <NoAccess what="чатам клиентов" />
    </div>
  );
}

/**
 * Ширина списка чатов на широком экране: тянется мышью за правый край, выбранная руками помнится
 * в браузере. По умолчанию — чтобы вкладки всех каналов влезали и с двузначными счётчиками.
 */
type PanelSide = "deal" | "contract" | "delivery" | "history" | "client" | "lk" | "qc" | "ai";
/** «Договор» — справа от «Сделки» и у любой сделки; «ЛК» — действия в кабинете; «Замечания»
 * контроля качества — последней, только с правом `qc_remark` или `knowledge_edit`. */
const PANEL_TABS: { id: PanelSide; label: string; title?: string }[] = [
  { id: "deal", label: "Сделка" },
  { id: "contract", label: "Договор", title: "Договор сделки: даты, товары, стоимость, оплата" },
  { id: "delivery", label: "Доставка", title: "Доставка к началу и к концу проката: адрес, карта, стоимость Яндекса и Dostavista" },
  { id: "history", label: "История" },
  { id: "client", label: "Товар", title: "Что понравилось клиенту: примерка, сайт, личный кабинет; что ему показывали" },
  { id: "lk", label: "ЛК", title: "Действия сотрудников в кабинете с этим чатом и сделкой" },
  { id: "qc", label: "Замечания", title: "Замечания контроля качества по клиенту: этот чат и связанные" },
  { id: "ai", label: "AI", title: "Кнопка AI: хронология черновиков, замечания AI Lab и сколько потрачено" },
];
const LIST_W = { min: 320, max: 760, base: 520, key: "prokat.chats.list-width" };
const clampW = (n: number) => Math.min(LIST_W.max, Math.max(LIST_W.min, Math.round(n)));
function savedListWidth(): number {
  try {
    const n = Number(localStorage.getItem(LIST_W.key));
    return n ? clampW(n) : LIST_W.base;
  } catch {
    return LIST_W.base; // хранилище закрыто — ширина по умолчанию
  }
}
// Переключатель над списком — «Все · Без ответа · AI · Задачи»: ширина по подписи, чтобы четыре влезали в узкий список.
const SEGMENT = "flex flex-auto items-center justify-center gap-1.5 whitespace-nowrap rounded-full px-2 text-[13px] font-medium leading-[26px]";
/** Выбор под «Задачи»: пересылки в салон, которые ещё в работе, или уже отмеченные завершёнными. */
// Отбор типов групп на «Группах» — свой у каждого сотрудника, помним в браузере: за какими группами
// он следит, то и считает число на вкладке.
const GROUP_TYPES_KEY = "prokat.chats.group-types";
function savedGroupTypes(): GroupType[] {
  try {
    const v: unknown = JSON.parse(localStorage.getItem(GROUP_TYPES_KEY) ?? "[]");
    return Array.isArray(v) ? GROUP_TYPES.map((g) => g.id).filter((id) => v.includes(id)) : [];
  } catch {
    return []; // хранилище закрыто — все группы
  }
}
const TASKS: { id: Exclude<TaskPick, "">; label: string; title: string }[] = [
  { id: "active", label: "Активные", title: "Чаты, где пересланное в салон ещё не отмечено завершённым" },
  { id: "done", label: "Завершённые", title: "Чаты, где задачу отметили завершённой за последние 30 дней" },
];
/** Выбор под «Без ответа»: где клиенту можно ответить. */
const PLACES: { id: ReplyPlace; label: string; title: string }[] = [
  { id: "", label: "Все", title: "Все чаты без ответа" },
  {
    id: "here",
    label: "Здесь",
    title:
      "Чаты, где ответ уходит клиенту прямо из кабинета: официальным входом канала (Wazzup, боты, Авито, ВК, Instagram) или через мост — с наших номеров в служебных браузерах",
  },
  {
    id: "web",
    label: "Браузер",
    title:
      "Переписка личного или рабочего аккаунта сотрудника, куда из кабинета ответить нельзя: здесь её только видно, ответить можно в его браузере — Telegram, MAX или WhatsApp Web",
  },
];

/** Закрыть карточку серии из поиска — убрать `?serial=` из адреса. */
const dropSerial = (setParams: SetURLSearchParams) =>
  setParams((prev) => {
    const p = new URLSearchParams(prev);
    p.delete("serial");
    return p;
  });

function Screen() {
  const chatId = Number(useParams().chatId) || null;
  const [search, setSearch] = useState("");
  const [needle, setNeedle] = useState("");
  // Выпадашка поиска (`SearchDrop`): «Чаты» — обычный поиск и последние звонки, «Серия» — то же поле
  // принимает номер серии, Enter — карточка серии (`?serial=` в адресе: при открытом чате её рисует
  // лента — с отправкой похожих в поле ответа); «Сделка / Клиент / Договор» — номер, Enter — чат
  // клиента (`GET /api/chats/number`, как клик по номеру в сообщении).
  const [drop, setDrop] = useState(false);
  const [searchMode, setSearchModeRaw] = useState<SearchMode>("chats");
  const [numberText, setNumberText] = useState("");
  const [numberNote, setNumberNote] = useState("");
  const setSearchMode = (m: SearchMode) => {
    setSearchModeRaw(m);
    setNumberNote("");
  };
  const dropRef = useRef<HTMLDivElement>(null);
  const [channel, setChannel] = useState<Channel | "">("");
  // «Личка / Группы»: групповая переписка сотрудников живёт рядом, но клиентский список по
  // умолчанию без неё — docs/specs/2026-10-10-chat-groups-design.md.
  const [kind, setKind] = useState<ChatKind>("person");
  // Типы групп, за которыми слежу («Общие», «Head», «Разные»); пусто — все.
  const [groupTypes, setGroupTypesRaw] = useState<GroupType[]>(savedGroupTypes);
  const toggleGroupType = (id: GroupType) => {
    const next = GROUP_TYPES.map((g) => g.id).filter((g) => (g === id ? !groupTypes.includes(g) : groupTypes.includes(g)));
    setGroupTypesRaw(next);
    try {
      localStorage.setItem(GROUP_TYPES_KEY, JSON.stringify(next));
    } catch {
      // хранилище закрыто — выбор живёт до перезагрузки
    }
  };
  // «Без ответа» — поверх канала: WA без ответа, TG без ответа…
  const [unanswered, setUnanswered] = useState(false);
  // …и сразу под ним выбор, где отвечать: здесь, через мост или только в браузере сотрудника.
  const [place, setPlace] = useState<ReplyPlace>("");
  const where: ReplyPlace = unanswered ? place : "";
  // «AI»: только чаты, где ответ ИИ ждёт решения — отправить, поправить или отклонить.
  const [drafts, setDrafts] = useState(false);
  // «Задачи»: чаты, из которых что-то переслано в салон, — активные или завершённые сотрудником.
  const [tasks, setTasks] = useState<TaskPick>("");
  // Фильтры панели — отчёт о выдаче, город, стадия сделки, рассылка, источник, куда писали, теги —
  // в адресе (`?report=…&city=…&stage=…&sent=…&source=…&account=…&tag=…`, повтором):
  // переживают перезагрузку и переход между чатами.
  const [params, setParams] = useSearchParams();
  const tags = params.getAll("tag");
  const cities = params.getAll("city");
  const accounts = params.getAll("account");
  const sources = params.getAll("source");
  const stages = params.getAll("stage");
  const mailings = params.getAll("sent");
  const reports = params.getAll("report");
  const panelKey = [tags, cities, accounts, sources, stages, mailings, reports].map((x) => x.join("\n")).join("\n\n");
  const picked = tags.length + cities.length + accounts.length + sources.length + stages.length + mailings.length + reports.length;
  const [filterMenu, setFilterMenu] = useState(false);
  const allTags = useQuery({ queryKey: ["client-chat-tags"], queryFn: loadChatTags, enabled: filterMenu || tags.length > 0 });
  // Счётчики фильтров справочные, считаются 2 с — держим 5 минут (и сервер тоже, аудит 21.10.26).
  const filters = useQuery({
    queryKey: ["client-chat-filters"],
    queryFn: loadChatFilters,
    enabled: filterMenu,
    staleTime: 5 * 60_000,
  });
  const setPicked = (next: Partial<Record<FilterKey, string[]>>) =>
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        for (const [key, values] of Object.entries(next)) {
          p.delete(key);
          values.forEach((v) => p.append(key, v));
        }
        return p;
      },
      { replace: true },
    );
  const toggle = (key: FilterKey, now: string[], value: string) =>
    setPicked({ [key]: now.includes(value) ? now.filter((x) => x !== value) : [...now, value] });
  // «Обзвон» (`?view=dial`): вместо чатов — лиды и сделки на мне, выбранный — `?dial=lead-N|deal-N`.
  // Разметка владельца 20.10.26, `components/chat/Dial.tsx`.
  const dialOn = params.get("view") === "dial";
  // «Звонки» (`?view=calls`): мои последние звонки, выбранный — `?dial=call-N`; строка и карточка —
  // те же, что у «Обзвона» (владелец 21.10.26, вместо «Всё»). Вместе — `sideOn`: чатов в списке нет.
  const callsOn = params.get("view") === "calls";
  const sideOn = dialOn || callsOn;
  // Выборка по ссылке со страницы вопроса базы знаний (`/chats?kbq=&days=`): чаты, а на «Звонках» —
  // звонки, где клиенты его задавали; чат открывается на сообщении-вопросе.
  const kbqParam = params.get("kbq");
  const kbqPick: KbqPick | null =
    kbqParam && !params.get("mailing") && !params.get("zapis") && !params.get("lsrc")
      ? { kbq: kbqParam, days: params.get("days") ?? "30" }
      : null;
  // `?dial=` без «Обзвона» — лид или сделка, найденные поиском по номеру без чата (`FoundByPhone`).
  const dialKey = params.get("dial");
  // «Все» (`&scope=all`) — только владельцу: все лиды «Не обработан» и сделки обзвона, не только мои.
  const dialScope: DialScope = params.get("scope") === "all" ? "all" : "mine";
  const dial = useQuery({
    queryKey: ["chat-dial", dialScope],
    queryFn: () => loadDial(dialScope),
    enabled: dialOn,
    // Порядок обзвона секундной свежести не требует; «Взять»/«Отдать» и статус лида перечитывают сразу.
    refetchInterval: 30_000,
  });
  // Каждый заход и опрос — сервер сначала докачивает мои свежие звонки из Bitrix: положил трубку —
  // звонок в списке.
  // «Мои / Все» — тот же `&scope=all`, что у «Обзвона»; у «Звонков» «Все» открыто каждому.
  const myCalls = useQuery({
    queryKey: ["chat-my-calls", dialScope, kbqPick?.kbq, kbqPick?.days],
    queryFn: () => loadMyCalls(dialScope, kbqPick),
    enabled: callsOn,
    // Звонки по вопросу — уже скачанные и размеченные: опрос не нужен.
    refetchInterval: kbqPick ? false : 30_000,
  });
  // Позвонили и сменили статус — строка уходит из списка, а карточка с «Звонить» остаётся открытой.
  const [dialPicked, setDialPicked] = useState<DialItem | null>(null);
  // Ссылку `?dial=found-deal-N` открыли заново (перезагрузка, ссылку прислали): найденного поиском в
  // памяти нет — объект и его номер спрашиваем по номеру сделки или лида (`GET /api/chats/number`).
  const lost = /^found-(deal|lead)-(\d+)$/.exec(dialKey ?? "");
  const restored = useQuery({
    queryKey: ["dial-restore", dialKey],
    queryFn: () => loadChatNumber(lost?.[2] ?? "", [lost?.[1] as "deal" | "lead"]),
    enabled: !!lost && dialPicked?.key !== dialKey && !sideOn,
    staleTime: 60_000,
  });
  const dialItem = dialKey
    ? (dial.data?.items.find((i) => i.key === dialKey) ??
      myCalls.data?.items.find((i) => i.key === dialKey) ??
      (dialPicked?.key === dialKey ? dialPicked : undefined) ??
      restoredDial(dialKey, restored.data))
    : undefined;
  const setView = (view: "dial" | "calls" | null) =>
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (view) {
          // Из «Обзвона» в «Звонки» и обратно: выбранная строка и «Мои / Все» прежней вкладки не держатся
          // («Все» обзвона — только владельцу).
          if (p.get("view") && p.get("view") !== view) ["dial", "scope"].forEach((k) => p.delete(k));
          p.set("view", view);
        } else ["view", "dial", "scope"].forEach((k) => p.delete(k));
        return p;
      },
      { replace: true },
    );
  const setDialOn = (on: boolean) => setView(on ? "dial" : null);
  // Полоска «Ждут ответа» под списком и её окно (`components/chat/WaitingBar.tsx`).
  const waitingQ = useWaiting();
  const hasLeads = !!waitingQ.data?.leads && waitingQ.data.leads.new + waitingQ.data.leads.redial > 0;
  const [waitingOpen, setWaitingOpen] = useState(false);
  const waitingBar = useRef<HTMLButtonElement>(null);
  const closeWaiting = useCallback(() => setWaitingOpen(false), []);
  const navigate = useNavigate();
  const { search: query } = useLocation(); // фильтры живут в адресе — не теряем при переходе к чату
  // Ширина списка: тянем правый край мышью, помним в браузере.
  const [listW, setListW] = useState(savedListWidth);
  const asideRef = useRef<HTMLElement>(null);
  const keepW = (width: number | null) => {
    try {
      if (width === null) localStorage.removeItem(LIST_W.key);
      else localStorage.setItem(LIST_W.key, String(width));
    } catch {
      // хранилище закрыто — ширина живёт до перезагрузки
    }
  };
  const dragW = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!e.currentTarget.hasPointerCapture(e.pointerId) || !asideRef.current) return;
    setListW(clampW(e.clientX - asideRef.current.getBoundingClientRect().left));
  };
  // Выборка по ссылке из «Рассылок»: получатели шаблона, дошедшие до шага, — тоже в адресе.
  const mailing = params.get("mailing");
  const pick: MailingPick | null = mailing
    ? { mailing, from: params.get("from") ?? "", to: params.get("to") ?? "", step: params.get("step") ?? "" }
    : null;
  // Выборка по ссылке из «Записей»: чаты, где сотрудник записал клиента.
  const zapis = params.get("zapis");
  const zapisPick: ZapisPick | null =
    !pick && zapis ? { zapis, zuser: params.get("zuser") ?? "", from: params.get("from") ?? "", to: params.get("to") ?? "" } : null;
  // Выборка по числу «Лиды → Источники»: чаты клиентов строки и колонки таблицы.
  const lsrc = params.get("lsrc");
  const leadsPick: LeadsPick | null =
    !pick && !zapisPick && lsrc
      ? { lsrc, lcol: params.get("lcol") ?? "", from: params.get("from") ?? "", to: params.get("to") ?? "" }
      : null;
  // Выборка по ссылке из «Рассылки → Переезд в Москву»: клиенты броней в одном состоянии.
  const moved = params.get("moved");
  const movedPick: MovedPick | null = !pick && !zapisPick && !leadsPick && moved ? { moved } : null;
  const pickKey = pick
    ? `${pick.mailing}|${pick.from}|${pick.to}|${pick.step}`
    : zapisPick
      ? `zapis|${zapisPick.zapis}|${zapisPick.zuser}|${zapisPick.from}|${zapisPick.to}`
      : leadsPick
        ? `lsrc|${leadsPick.lsrc}|${leadsPick.lcol}|${leadsPick.from}|${leadsPick.to}`
        : movedPick
          ? `moved|${movedPick.moved}`
          : kbqPick
            ? `kbq|${kbqPick.kbq}|${kbqPick.days}`
            : "";
  const dropPick = () =>
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        ["mailing", "from", "to", "step", "zapis", "zuser", "lsrc", "lcol", "moved", "kbq", "days", "msg"].forEach((k) => p.delete(k));
        return p;
      },
      { replace: true },
    );
  // Поиск — на сервере (чатов тысячи), поэтому ждём паузу в наборе; от 3 знаков: цифры ищутся
  // подстрокой номера по всем чатам, 0,8 с на запрос (аудит 21.10.26).
  useEffect(() => {
    const t = setTimeout(() => {
      const v = search.trim();
      setNeedle(v.length >= 3 ? v : "");
      // Номер целиком ищем по всем каналам: нужен клиент, а не его чаты в одном канале.
      if (searchPhone(v)) setChannel("");
    }, 500);
    return () => clearTimeout(t);
  }, [search]);
  const list = useQuery({
    queryKey: ["client-chats", channel, needle, unanswered, where, panelKey, pickKey, drafts, tasks, kind, groupTypes.join()],
    queryFn: () =>
      loadChats(channel, needle, unanswered, { tags, cities, accounts, sources, stages, mailings, reports }, where, pick ?? zapisPick ?? leadsPick ?? movedPick ?? kbqPick, drafts, tasks, kind, groupTypes),
    refetchInterval: 6000,
    placeholderData: (prev) => prev,
    enabled: !sideOn,
  });
  // Числа на «Личке» и «Группах» — своим лёгким опросом: на «Обзвоне» и «Звонках» список не
  // грузится, а пришедшее сообщение должно быть видно (просьба КЦ 25.10.26).
  const badges = useQuery({
    queryKey: ["client-chat-badges", groupTypes.join()],
    queryFn: () => loadChatBadges(groupTypes),
    refetchInterval: 6000,
    placeholderData: (prev) => prev,
  });
  const chats = list.data?.chats ?? [];
  // Всплывашка «Входящий» (`components/chat/IncomingCalls.tsx`): клик — номер в поиск, как набранный
  // руками; поиск дошёл — открыть первый чат номера, а без чата — первую карточку лида или сделки
  // номера из того же поиска, что у `FoundByPhone`. Спека `docs/specs/2026-10-21-call-live-design.md`.
  const [incoming, setIncoming] = useState("");
  // Номер, чей верхний чат уже открыли сами (ниже, «набрали номер»).
  const phoneOpened = useRef("");
  const openIncoming = (phone: string) => {
    setSearch(phone);
    setNeedle(phone);
    setIncoming(phone);
    const p = new URLSearchParams(query);
    ["view", "dial", "scope"].forEach((k) => p.delete(k));
    navigate({ pathname: "/chats", search: p.toString() });
  };
  // Поиск по номеру сделки, клиента или договора CRM: чат клиента → его телефон в поиск → карточка
  // сделки; вид один — найдено не больше одного. Открыли — поле снова «Чаты» (там телефон, если по нему).
  const openNumber = useOpenNumber(openIncoming);
  const openByNumber = async (kind: Exclude<SearchMode, "chats" | "serial">, n: string, input: HTMLInputElement) => {
    const label = NUMBER_MODES[kind].label;
    setNumberNote("Ищу…");
    try {
      const r = await loadChatNumber(n, [kind]);
      const item = r.items[0];
      if (item && openNumber.open(item)) {
        setSearchMode("chats");
        setDrop(false);
        input.blur();
      } else if (item) setNumberNote(`${item.title}: нет ни чата, ни телефона, ни сделки`);
      else if (r.errors.length) setNumberNote("CRM не ответила — попробуйте ещё раз");
      else setNumberNote(`${label} ${n} не найден${kind === "deal" ? "а" : ""}`);
    } catch {
      setNumberNote("Не удалось проверить номер — попробуйте ещё раз");
    }
  };
  const incomingReady = !!incoming && needle === incoming && !!list.data && !list.isPlaceholderData;
  const incomingFound = useQuery({
    queryKey: ["quick-jump", incoming],
    queryFn: () => loadSearch(incoming),
    staleTime: 60_000,
    enabled: incomingReady && chats.length === 0 && !!searchPhone(incoming),
  });
  useEffect(() => {
    if (!incomingReady) return;
    if (chats.length) {
      setIncoming("");
      phoneOpened.current = incoming;
      navigate({ pathname: `/chats/${chats[0]!.id}`, search: query });
      return;
    }
    if (!searchPhone(incoming)) return setIncoming("");
    if (!incomingFound.data && !incomingFound.isError) return;
    setIncoming("");
    // Ни чата, ни лидов, ни сделок — карточка номера с формой «Создать сделку».
    const first = incomingFound.data ? (foundItems(incomingFound.data, incoming)[0] ?? phoneItem(incoming)) : undefined;
    if (!first) return;
    setDialPicked(first);
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.set("dial", first.key);
        return p;
      },
      { replace: true },
    );
  }, [incomingReady, chats, incoming, incomingFound.data, incomingFound.isError, navigate, query, setParams]);
  // Набрали номер, и чаты нашлись — сразу открыть верхний (последний по переписке) общей лентой «Все»
  // (`Thread` `allFirst`): набирают номер ради клиента, а не списка (владелец 22.10.26). Один раз на номер —
  // дальше чаты выбирают руками; всплывашка «Входящий» открывает сама и отмечает номер в `phoneOpened`.
  const phoneReady = !!searchPhone(needle) && !!list.data && !list.isPlaceholderData && chats.length > 0;
  useEffect(() => {
    if (!searchPhone(needle)) phoneOpened.current = "";
    if (!phoneReady || incoming || phoneOpened.current === needle) return;
    phoneOpened.current = needle;
    const p = new URLSearchParams(query);
    p.delete("dial");
    navigate({ pathname: `/chats/${chats[0]!.id}`, search: p.toString() });
  }, [phoneReady, needle, incoming, chats, query, navigate]);
  const counts = list.data?.counts ?? {};
  const sum = (by: Partial<Record<Channel, number>>) => CHANNELS.reduce((n, c) => n + (by[c.id] ?? 0), 0);
  const total = sum(counts);
  const unansweredBy = list.data?.unanswered ?? {};
  const waiting = channel ? (unansweredBy[channel] ?? 0) : sum(unansweredBy);
  const webBy = list.data?.unanswered_web ?? {};
  const waitingWeb = channel ? (webBy[channel] ?? 0) : sum(webBy);
  const draftCount = list.data?.suggest_count;
  const taskCount: Record<Exclude<TaskPick, "">, number> = { active: list.data?.task_count ?? 0, done: list.data?.task_done_count ?? 0 };
  // Числа на «Личке» и «Обзвоне» видны с любой вкладки — переключиться и проверить (разметка 21.10.26).
  const kindCount: Partial<Record<ChatKind, number>> = {
    person: badges.data?.unanswered_person ?? 0,
    group: badges.data?.unread_groups ?? 0,
  };
  const callbacks = waitingQ.data?.callback?.count ?? 0;
  const dialCount = callbacks || (waitingQ.data?.leads?.new ?? 0);
  const placeCount: Record<ReplyPlace, number> = {
    "": waiting,
    here: waiting - waitingWeb,
    web: waitingWeb,
  };
  const segment = (value: boolean, label: string, n = 0) => (
    <button
      type="button"
      onClick={() => {
        setUnanswered(value);
        setDrafts(false);
        setTasks("");
      }}
      title={value ? "Только чаты, где последнее сообщение — клиента и мы ещё не ответили" : "Все чаты"}
      className={`${SEGMENT} ${unanswered === value && !drafts && !tasks ? "bg-[var(--tg-primary)] text-white" : "text-[var(--tg-sec)] hover:text-[var(--tg-text)]"}`}
    >
      {label}
      {n > 0 && (
        <span
          className={`rounded-full px-1.5 text-[12px] leading-[18px] ${unanswered === value && !drafts && !tasks ? "bg-white text-[var(--tg-primary)]" : "bg-[var(--tg-sec)] text-white"}`}
        >
          {n}
        </span>
      )}
    </button>
  );
  const tab = (id: Channel | "", label: string, n: number) => (
    <button
      key={id}
      type="button"
      onClick={() => setChannel(id)}
      className={`flex flex-none items-center gap-1 border-b-[3px] px-1 pb-2 pt-1 text-[14px] font-medium ${
        channel === id ? "border-[var(--tg-primary)] text-[var(--tg-primary)]" : "border-transparent text-[var(--tg-sec)]"
      }`}
    >
      {label}
      {n > 0 && (
        <span
          className={`rounded-full px-1.5 text-[12px] leading-[18px] text-white ${channel === id ? "bg-[var(--tg-primary)]" : "bg-[var(--tg-sec)]"}`}
        >
          {n}
        </span>
      )}
    </button>
  );

  return (
    <div className="tgc flex h-dvh gap-2 overflow-hidden p-0 md:p-2">
      <aside
        ref={asideRef}
        style={{ "--list-w": `${listW}px` } as CSSProperties}
        className={`${chatId || dialItem ? "hidden md:flex" : "flex"} relative w-full flex-none flex-col bg-[var(--tg-surface)] md:w-[var(--list-w)] md:max-w-[60vw] md:rounded-[24px] md:shadow-[0_0_4px_rgba(0,0,0,.24)]`}
      >
        <div className="flex h-14 flex-none items-center gap-2 px-3">
          <Link
            to="/"
            title="В кабинет"
            className="grid size-10 flex-none place-items-center rounded-full text-[var(--tg-sec)] hover:bg-[var(--tg-hover)]"
          >
            <Menu size={22} />
          </Link>
          <div ref={dropRef} className="relative min-w-0 flex-1">
            <label className="flex h-11 items-center gap-3 rounded-[22px] bg-[var(--tg-input)] px-4 text-[var(--tg-sec)]">
              {searchMode === "serial" ? (
                <Shirt size={20} className="flex-none" />
              ) : searchMode === "chats" ? (
                <Search size={20} className="flex-none" />
              ) : (
                <Hash size={20} className="flex-none" />
              )}
              <input
                value={searchMode === "chats" ? search : numberText}
                onChange={(e) => {
                  if (searchMode === "chats") return setSearch(e.target.value);
                  setNumberText(e.target.value.replace(/\D/g, ""));
                  setNumberNote("");
                }}
                onFocus={() => setDrop(true)}
                onKeyDown={(e) => {
                  if (searchMode === "chats" || e.key !== "Enter" || !Number(numberText)) return;
                  if (searchMode === "serial") {
                    setParams((prev) => {
                      const p = new URLSearchParams(prev);
                      p.set("serial", numberText);
                      return p;
                    });
                    setDrop(false);
                    e.currentTarget.blur();
                    return;
                  }
                  openByNumber(searchMode, numberText, e.currentTarget);
                }}
                placeholder={searchMode === "chats" ? "Поиск" : NUMBER_MODES[searchMode].placeholder}
                inputMode={searchMode === "chats" ? undefined : "numeric"}
                className="min-w-0 flex-1 bg-transparent text-[var(--tg-text)] outline-none placeholder:text-[var(--tg-sec)]"
              />
            </label>
            {drop && (
              <SearchDrop
                anchor={dropRef}
                mode={searchMode}
                setMode={setSearchMode}
                note={numberNote}
                onPhone={(phone) => {
                  setDrop(false);
                  openIncoming(phone);
                }}
                onClose={() => setDrop(false)}
              />
            )}
          </div>
          {/* Фильтры: город, стадия сделки, рассылка (какую отправляли и что нажал клиент), источник, наш аккаунт «куда писали», теги — внутри раздела любой из отмеченных,
              разделы складываются; поверх канала, поиска и «Без ответа», сужают и счётчики.
              Панель — `components/chat/ChatFilters`: ключевые слова сверху, ниже комбобоксы. */}
          <ChatFilters
            open={filterMenu}
            onOpenChange={setFilterMenu}
            channels={CHANNELS}
            cities={cities}
            accounts={accounts}
            sources={sources}
            stages={stages}
            mailings={mailings}
            reports={reports}
            tags={tags}
            picked={picked}
            setPicked={setPicked}
            toggle={toggle}
            filters={filters}
            allTags={allTags}
          />
        </div>
        <IncomingCalls onOpen={openIncoming} />
        {/* Выборка по ссылке из «Рассылок»: чьи это чаты и как вернуться ко всем. */}
        {pick && (
          <div
            className="mx-3 mb-2 flex flex-none items-center gap-2 rounded-[12px] bg-[var(--tg-primary)]/10 py-1.5 pl-3 pr-1.5 text-[13px] leading-[18px]"
            data-annot="выборка по рассылке"
          >
            <div className="min-w-0 flex-1">
              <div className="truncate font-medium">Рассылка: {list.data?.mailing?.title ?? "…"}</div>
              <div className="text-[var(--tg-sec)]">
                {[
                  PICK_STEP[pick.step] ?? "отправлено",
                  pick.from === pick.to ? dm(pick.from) : `${dm(pick.from)}–${dm(pick.to)}`,
                  list.data?.mailing ? pickCount(list.data.mailing.sends, list.data.mailing.count) : "",
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
            </div>
            <button
              type="button"
              onClick={dropPick}
              title="Показать все чаты"
              className="grid size-8 flex-none place-items-center rounded-full text-[var(--tg-sec)] hover:bg-[var(--tg-hover)]"
            >
              <X size={18} />
            </button>
          </div>
        )}
        {/* Выборка по ссылке из «Записей»: чьи записи и как вернуться ко всем чатам. */}
        {zapisPick && (
          <div
            className="mx-3 mb-2 flex flex-none items-center gap-2 rounded-[12px] bg-[var(--tg-primary)]/10 py-1.5 pl-3 pr-1.5 text-[13px] leading-[18px]"
            data-annot="выборка по записям"
          >
            <div className="min-w-0 flex-1">
              <div className="truncate font-medium">
                Записали: {list.data?.zapis ? list.data.zapis.user_name || "все сотрудники" : "…"}
              </div>
              <div className="text-[var(--tg-sec)]">
                {[
                  list.data?.zapis?.label,
                  zapisPick.from === zapisPick.to ? dm(zapisPick.from) : `${dm(zapisPick.from)}–${dm(zapisPick.to)}`,
                  list.data?.zapis ? zapisCount(list.data.zapis.people, list.data.zapis.count) : "",
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
            </div>
            <button
              type="button"
              onClick={dropPick}
              title="Показать все чаты"
              className="grid size-8 flex-none place-items-center rounded-full text-[var(--tg-sec)] hover:bg-[var(--tg-hover)]"
            >
              <X size={18} />
            </button>
          </div>
        )}
        {/* Выборка по числу «Лиды → Источники»: строка и колонка таблицы, как вернуться ко всем чатам. */}
        {leadsPick && (
          <div
            className="mx-3 mb-2 flex flex-none items-center gap-2 rounded-[12px] bg-[var(--tg-primary)]/10 py-1.5 pl-3 pr-1.5 text-[13px] leading-[18px]"
            data-annot="выборка по источникам лидов"
          >
            <div className="min-w-0 flex-1">
              <div className="truncate font-medium">Лиды: {list.data?.lsrc?.label ?? "…"}</div>
              <div className="text-[var(--tg-sec)]">
                {[
                  SOURCE_COL[leadsPick.lcol]?.head,
                  leadsPick.from === leadsPick.to ? dm(leadsPick.from) : `${dm(leadsPick.from)}–${dm(leadsPick.to)}`,
                  list.data?.lsrc ? leadsCount(list.data.lsrc) : "",
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
            </div>
            <Link
              to={`/leads?tab=sources&from=${leadsPick.from}&to=${leadsPick.to}${leadsPick.lsrc.startsWith("a:") ? "&view=advert" : ""}`}
              title="Вернуться к таблице «Источники»"
              className="flex-none text-[12px] text-[var(--tg-sec)] hover:underline"
            >
              к таблице
            </Link>
            <button
              type="button"
              onClick={dropPick}
              title="Показать все чаты"
              className="grid size-8 flex-none place-items-center rounded-full text-[var(--tg-sec)] hover:bg-[var(--tg-hover)]"
            >
              <X size={18} />
            </button>
          </div>
        )}
        {/* Выборка по ссылке из «Рассылки → Переезд в Москву»: состояние броней, назад к таблице. */}
        {movedPick && (
          <div
            className="mx-3 mb-2 flex flex-none items-center gap-2 rounded-[12px] bg-[var(--tg-primary)]/10 py-1.5 pl-3 pr-1.5 text-[13px] leading-[18px]"
            data-annot="выборка по переезду"
          >
            <div className="min-w-0 flex-1">
              <div className="truncate font-medium">Переезд в Москву: {list.data?.moved?.label ?? "…"}</div>
              <div className="text-[var(--tg-sec)]">{list.data?.moved ? movedCount(list.data.moved) : ""}</div>
            </div>
            <Link
              to={`/mailing?tab=moved${movedPick.moved.startsWith("zapis:") ? "&who=zapis" : ""}${
                movedPick.moved.endsWith("all") ? "" : `&state=${movedPick.moved.replace("zapis:", "")}`
              }`}
              title="Вернуться к таблице «Переезд в Москву»"
              className="flex-none text-[12px] text-[var(--tg-sec)] hover:underline"
            >
              к таблице
            </Link>
            <button
              type="button"
              onClick={dropPick}
              title="Показать все чаты"
              className="grid size-8 flex-none place-items-center rounded-full text-[var(--tg-sec)] hover:bg-[var(--tg-hover)]"
            >
              <X size={18} />
            </button>
          </div>
        )}
        {/* Выборка по вопросу базы знаний: формулировка, чаты ↔ звонки, вернуться ко всем чатам. */}
        {kbqPick && (
          <KbqBanner
            info={(callsOn ? myCalls.data?.kbq : list.data?.kbq) ?? null}
            calls={callsOn}
            onView={(calls) => setView(calls ? "calls" : null)}
            onDrop={dropPick}
          />
        )}
        {/* Личка клиентов или групповые чаты сотрудников: списки разные, по умолчанию личка. */}
        <div className="mx-3 mb-1.5 flex flex-none rounded-full bg-[var(--tg-input)] p-0.5" data-annot="вид чатов">
          {(
            [
              ["person", "Личка", "Переписка с клиентами"],
              ["group", "Группы", "Групповые чаты сотрудников: «Общее <город>», «Head …», «Новости КЦ»"],
            ] as const
          )
            // Группы видят все: в списке — те, где сотрудник участник, и открытые всем («Общее <город>»).
            .map(([id, label, hint]) => (
            <button
              key={id}
              type="button"
              onClick={() => {
                setKind(id);
                setUnanswered(false);
                setDrafts(false);
                setTasks("");
                if (sideOn) setView(null);
              }}
              title={hint}
              className={`${SEGMENT} ${kind === id && !sideOn ? "bg-[var(--tg-primary)] text-white" : "text-[var(--tg-sec)] hover:text-[var(--tg-text)]"}`}
              data-annot={`вид чатов «${label}»`}
            >
              {label}
              {(kindCount[id] ?? 0) > 0 && (
                <span
                  title={id === "group" ? "Групп с непрочитанными: после того, как вы открыли группу, написали другие" : "Без ответа: здесь и через мост"}
                  className={`rounded-full px-1.5 text-[12px] leading-[18px] ${kind === id && !sideOn ? "bg-white text-[var(--tg-primary)]" : "bg-[var(--tg-sec)] text-white"}`}
                >
                  {kindCount[id]}
                </span>
              )}
            </button>
          ))}
          {/* «Звонки»: мои последние звонки по времени — клик открывает клиента, как у «Обзвона». */}
          <button
            type="button"
            onClick={() => setView("calls")}
            title="Мои последние звонки, свежие сверху: при заходе подтягиваем их из Bitrix — только что положили трубку, звонок уже здесь. Клик — переписка клиента или его лид и сделка"
            className={`${SEGMENT} ${callsOn ? "bg-[var(--tg-primary)] text-white" : "text-[var(--tg-sec)] hover:text-[var(--tg-text)]"}`}
            data-annot="вид чатов «Звонки»"
          >
            <PhoneCall size={14} /> Звонки
          </button>
          {/* «Обзвон»: лиды и сделки на мне — клик открывает всю переписку клиента и «Звонить». */}
          <button
            type="button"
            onClick={() => setDialOn(true)}
            title="Кого мне обзвонить: лиды «Не обработан» и сделки обзвона на мне. Клик — вся переписка клиента и кнопка «Звонить» для софтфона"
            className={`${SEGMENT} ${dialOn ? "bg-[var(--tg-primary)] text-white" : "text-[var(--tg-sec)] hover:text-[var(--tg-text)]"}`}
            data-annot="вид чатов «Обзвон»"
          >
            <Phone size={14} /> Обзвон
            {/* Красным — ждут перезвона после пропущенного; их нет — новые лиды, ждущие звонка. */}
            {dialCount > 0 && (
              <span
                title={callbacks ? "Ждут перезвона после пропущенного входящего" : "Новых лидов ждут звонка"}
                className={`rounded-full px-1.5 text-[12px] leading-[18px] ${
                  callbacks ? "bg-[#e53935] text-white" : dialOn ? "bg-white text-[var(--tg-primary)]" : "bg-[var(--tg-sec)] text-white"
                }`}
              >
                {dialCount}
              </span>
            )}
          </button>
        </div>
        {/* Типы групп — нажимаются независимо: включил «Общие» и «Head» — в списке и в числе на
            «Группах» только они; ничего не нажато — все группы. Число — сколько групп, синим — непрочитанные. */}
        {kind === "group" && !sideOn && (
          <div className="mx-3 mb-1.5 flex flex-none gap-1.5" data-annot="типы групп">
            {GROUP_TYPES.map((g) => {
              const on = groupTypes.includes(g.id);
              const n = list.data?.group_types_count?.[g.id];
              return (
                <button
                  key={g.id}
                  type="button"
                  aria-pressed={on}
                  onClick={() => toggleGroupType(g.id)}
                  title={`${g.title}${n ? ` · групп ${n.chats}, непрочитанных ${n.unread}` : ""}. Нажмите, чтобы ${on ? "убрать из" : "оставить в"} списке`}
                  className={`flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-full border px-2 text-[12px] font-medium leading-[24px] ${
                    on
                      ? "border-[var(--tg-primary)] bg-[var(--tg-primary)]/10 text-[var(--tg-primary)]"
                      : "border-transparent bg-[var(--tg-input)] text-[var(--tg-sec)] hover:text-[var(--tg-text)]"
                  }`}
                  data-annot={`тип групп «${g.label}»`}
                >
                  {g.label}
                  {n && <span className="opacity-70">{n.chats}</span>}
                  {!!n?.unread && (
                    <span className="rounded-full bg-[var(--tg-primary)] px-1.5 text-[11px] leading-[16px] text-white">{n.unread}</span>
                  )}
                </button>
              );
            })}
          </div>
        )}
        {/* Отдельный фильтр над каналами, всегда на виду: все чаты или только ждущие ответа. */}
        <div className={`mx-3 mb-2 flex flex-none rounded-full bg-[var(--tg-input)] p-0.5 ${sideOn ? "hidden" : ""}`}>
          {segment(false, "Все")}
          {segment(true, "Без ответа", waiting)}
          {/* Отбор «AI» — тем, кто видит ответы ИИ (сервер прислал их число). */}
          {draftCount !== undefined && (
            <button
              type="button"
              onClick={() => {
                setDrafts(true);
                setUnanswered(false);
                setTasks("");
              }}
              title="Только чаты, где ИИ подготовил ответ и он ждёт решения: отправить, поправить или отклонить"
              className={`${SEGMENT} ${drafts ? "bg-[var(--tg-primary)] text-white" : "text-[var(--tg-sec)] hover:text-[var(--tg-text)]"}`}
              data-annot="отбор: AI"
            >
              <Sparkles size={14} /> AI
              {draftCount > 0 && (
                <span
                  className={`rounded-full px-1.5 text-[12px] leading-[18px] ${drafts ? "bg-white text-[var(--tg-primary)]" : "bg-[var(--tg-sec)] text-white"}`}
                >
                  {draftCount}
                </span>
              )}
            </button>
          )}
          {/* Отбор «Задачи»: чаты, из которых что-то переслано в салон (подбор, сдать платье, сообщение
              клиента); число — чатов с активными задачами. */}
          <button
            type="button"
            onClick={() => {
              setTasks("active");
              setDrafts(false);
              setUnanswered(false);
            }}
            title="Только чаты с задачами — пересылками в салон: активные, пока сотрудник не проверит и не отметит «Завершено»"
            className={`${SEGMENT} ${tasks ? "bg-[var(--tg-primary)] text-white" : "text-[var(--tg-sec)] hover:text-[var(--tg-text)]"}`}
            data-annot="отбор: задачи"
          >
            <ClipboardList size={14} /> Задачи
            {taskCount.active > 0 && (
              <span className={`rounded-full px-1.5 text-[12px] leading-[18px] ${tasks ? "bg-white text-[var(--tg-primary)]" : "bg-[var(--tg-sec)] text-white"}`}>
                {taskCount.active}
              </span>
            )}
          </button>
        </div>
        {/* «Задачи» — активные или уже завершённые сотрудником. */}
        {tasks && !sideOn && (
          <div className="mx-3 mb-2 flex flex-none gap-1.5" data-annot="статус задач">
            {TASKS.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTasks(t.id)}
                title={t.title}
                className={`flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-full border px-2 text-[12px] font-medium leading-[24px] ${
                  tasks === t.id
                    ? "border-[var(--tg-primary)] bg-[var(--tg-primary)]/10 text-[var(--tg-primary)]"
                    : "border-transparent bg-[var(--tg-input)] text-[var(--tg-sec)] hover:text-[var(--tg-text)]"
                }`}
              >
                {t.label}
                <span>{taskCount[t.id]}</span>
              </button>
            ))}
          </div>
        )}
        {/* «Без ответа» сразу спрашивает, где отвечать: часть чатов ведём здесь, в чаты наших номеров
            ответ уходит через мост, а переписку личных аккаунтов сотрудников видим, но ответить
            можно только в их браузере. */}
        {unanswered && !sideOn && (
          <div className="mx-3 mb-2 flex flex-none gap-1.5" data-annot="где ответить">
            {PLACES.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setPlace(p.id)}
                title={p.title}
                className={`flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-full border px-2 text-[12px] font-medium leading-[24px] ${
                  place === p.id
                    ? "border-[var(--tg-primary)] bg-[var(--tg-primary)]/10 text-[var(--tg-primary)]"
                    : "border-transparent bg-[var(--tg-input)] text-[var(--tg-sec)] hover:text-[var(--tg-text)]"
                }`}
              >
                {p.label}
                <span>{placeCount[p.id]}</span>
              </button>
            ))}
          </div>
        )}
        <div className={`flex flex-none gap-1.5 overflow-x-auto px-3 pb-1 ${sideOn ? "hidden" : ""}`}>
          {tab("", "Все", total)}
          {CHANNELS.map((c) => tab(c.id, c.short, counts[c.id] ?? 0))}
        </div>
        {dialOn && (
          <DialTools
            scope={dialScope}
            allAllowed={dial.data?.all_allowed ?? false}
            onScope={(scope) =>
              setParams(
                (prev) => {
                  const p = new URLSearchParams(prev);
                  if (scope === "all") p.set("scope", "all");
                  else p.delete("scope");
                  return p;
                },
                { replace: true },
              )
            }
          />
        )}
        {dialOn && (
          <div className="min-h-0 flex-1 overflow-y-auto p-2" data-annot="список обзвона">
            {dial.isPending && <p className="p-4 text-[var(--tg-sec)]">Загрузка…</p>}
            {dial.isError && <p className="p-4 text-[#e53935]">Не удалось загрузить обзвон: {dial.error.message}</p>}
            {(dial.data?.errors ?? []).map((e) => (
              <p key={e} className="mx-2 mb-1 text-[13px] text-[#b26a00]">
                {e}
              </p>
            ))}
            {dial.data && dial.data.items.length === 0 && (
              <p className="p-4 text-[var(--tg-sec)]">
                {dialScope === "all"
                  ? "Лидов «Не обработан» и сделок обзвона сейчас нет ни на ком."
                  : "На вас сейчас нет лидов «Не обработан» и сделок обзвона. Взять себе — кнопка «Взять» вверху."}
              </p>
            )}
            {(dial.data?.items ?? []).map((it) => (
              <DialRow key={it.key} item={it} active={it.key === dialKey} onPick={setDialPicked} />
            ))}
          </div>
        )}
        {callsOn && !kbqPick && (
          <div className="mx-3 mb-2 flex flex-none rounded-full bg-[var(--tg-input)] p-0.5" data-annot="звонки: мои или все">
            {(
              [
                ["mine", "Мои", "Мои звонки"],
                ["all", "Все", "Звонки всех сотрудников — у каждого видно, кто разговаривал"],
              ] as const
            ).map(([id, label, hint]) => (
              <button
                key={id}
                type="button"
                title={hint}
                onClick={() =>
                  setParams(
                    (prev) => {
                      const p = new URLSearchParams(prev);
                      if (id === "all") p.set("scope", "all");
                      else p.delete("scope");
                      return p;
                    },
                    { replace: true },
                  )
                }
                className={`${SEGMENT} ${dialScope === id ? "bg-[var(--tg-primary)] text-white" : "text-[var(--tg-sec)] hover:text-[var(--tg-text)]"}`}
              >
                {label}
              </button>
            ))}
          </div>
        )}
        {callsOn && (
          <div className="min-h-0 flex-1 overflow-y-auto p-2" data-annot="список звонков">
            {myCalls.isPending && <p className="p-4 text-[var(--tg-sec)]">Подтягиваю звонки из Bitrix…</p>}
            {myCalls.isError && <p className="p-4 text-[#e53935]">Не удалось загрузить звонки: {myCalls.error.message}</p>}
            {(myCalls.data?.errors ?? []).map((e) => (
              <p key={e} className="mx-2 mb-1 text-[13px] text-[#b26a00]">
                {e}
              </p>
            ))}
            {myCalls.data && myCalls.data.items.length === 0 && (
              <p className="p-4 text-[var(--tg-sec)]">
                {kbqPick ? "За период звонков с этим вопросом нет." : dialScope === "all" ? "За неделю звонков нет." : "За неделю ваших звонков нет."}
              </p>
            )}
            {(myCalls.data?.items ?? []).map((it) => (
              <CallRow key={it.key} item={it} active={it.key === dialKey} onPick={setDialPicked} />
            ))}
          </div>
        )}
        <div className={`min-h-0 flex-1 overflow-y-auto p-2 ${sideOn ? "hidden" : ""}`}>
          {list.isPending && <p className="p-4 text-[var(--tg-sec)]">Загрузка…</p>}
          {list.isError && <p className="p-4 text-[var(--tg-sec)]">Не удалось загрузить чаты</p>}
          {/* Номер, и чаты с ним есть: лиды и сделки номера в Bitrix — кнопкой, окном. */}
          {chats.length > 0 && searchPhone(needle) && (
            <FoundByPhoneButton phone={searchPhone(needle)} active={dialKey} onPick={setDialPicked} />
          )}
          {/* Номер, а чата с ним нет: лиды и сделки номера в Bitrix — открыть карточку, как в «Обзвоне». */}
          {list.data && chats.length === 0 && searchPhone(needle) ? (
            <FoundByPhone phone={searchPhone(needle)} active={dialKey} onPick={setDialPicked} />
          ) : list.data && chats.length === 0 && (
            <p className="p-4 text-[var(--tg-sec)]">
              {needle
                ? "Ничего не найдено"
                : drafts
                  ? "Ответов AI, ждущих решения, нет"
                : tasks
                  ? tasks === "done" ? "Завершённых задач за 30 дней нет" : "Активных задач нет: всё, что пересылали в салон, завершено"
                : picked
                  ? "По выбранным фильтрам чатов нет"
                  : unanswered
                    ? where === "web" ? "В браузере всем ответили" : where === "here" ? "Здесь всем ответили" : "Всем ответили"
                    : pick ? "В этой выборке чатов нет" : "Сообщений пока нет"}
            </p>
          )}
          {chats.map((c) => (
            <Fragment key={c.id}>
              <ChatRow chat={c} active={c.id === chatId} msg={list.data?.kbq?.messages[String(c.id)]} />
              {/* Под выбранным чатом — остальные чаты этого клиента (та же сделка или номер);
                  в выборке по рассылке — под каждым: получатель виден во всех своих каналах. */}
              {(c.id === chatId || pick) && (c.related ?? []).length > 0 && (
                <div className="mb-1 ml-[30px] border-l-2 border-[var(--tg-primary)]/40 pl-2" data-annot="связанные чаты в списке">
                  {c.related.map((r) => (
                    <RelatedRow key={r.id} chat={r} active={r.id === chatId} />
                  ))}
                </div>
              )}
            </Fragment>
          ))}
        </div>
        {/* Полоска «Ждут ответа»: не отбор списка, а счётчик «дольше 5 минут, мои и бесхозные» —
            фильтры на неё не влияют; клик — окно с давностью и тем, где ответить. */}
        {/* Над ней — «ждут перезвона» (только когда кто-то ждёт): клик — «Обзвон», где эти номера сверху. */}
        <CallbackBar variant="tgc" data={waitingQ.data?.callback} open={dialOn} onToggle={() => setDialOn(!dialOn)} />
        <WaitingBar ref={waitingBar} variant="tgc" q={waitingQ} open={waitingOpen} onToggle={() => setWaitingOpen((was) => !was)} className={hasLeads ? "" : "md:rounded-b-[24px]"} />
        {/* Под ней — лиды, ждущие дозвона (очередь «Распределения»): клик — «Обзвон», там «Взять». */}
        <LeadsBar variant="tgc" data={waitingQ.data?.leads} onOpen={() => setDialOn(true)} className="md:rounded-b-[24px]" />
        {waitingOpen && waitingBar.current && waitingQ.data && (
          <Card
            anchor={waitingBar.current}
            close={closeWaiting}
            label="Чаты, которые ждут ответа"
            title={`Ждут ответа дольше ${waitingQ.data.threshold} минут`}
            sub={String(waitingQ.data.count)}
            foot={<span>В счёте — общие чаты, где отвечают отсюда, и чаты наших номеров, открытых у вас или ни у кого. Фильтры списка на него не влияют.</span>}
          >
            <div className="-mx-1 max-h-[min(60vh,520px)] overflow-y-auto px-1">
              <WaitingList
                data={waitingQ.data}
                onChat={(row) => {
                  setWaitingOpen(false);
                  void navigate({ pathname: `/chats/${row.id}`, search: query });
                }}
              />
            </div>
          </Card>
        )}
        {/* Правый край тянется мышью — чтобы влезали все каналы; двойной клик — ширина по умолчанию. */}
        <div
          role="separator"
          aria-orientation="vertical"
          title="Потяните, чтобы изменить ширину списка; двойной клик — как было"
          onPointerDown={(e) => {
            e.preventDefault();
            e.currentTarget.setPointerCapture(e.pointerId);
          }}
          onPointerMove={dragW}
          onPointerUp={() => keepW(listW)}
          onDoubleClick={() => {
            setListW(LIST_W.base);
            keepW(null);
          }}
          className="group absolute -right-2 top-0 z-[5] hidden h-full w-2 cursor-col-resize touch-none md:block"
        >
          <div className="mx-auto h-full w-[3px] rounded-full group-hover:bg-[var(--tg-primary)]/40 group-active:bg-[var(--tg-primary)]" />
        </div>
      </aside>

      <section className={`${chatId || dialItem ? "flex" : "hidden md:flex"} min-w-0 flex-1`}>
        {chatId ? (
          <OpenPhone.Provider value={openIncoming}>
            <Thread key={chatId} chatId={chatId} dial={sideOn ? dialItem : undefined} allFirst={!!searchPhone(needle)} />
          </OpenPhone.Provider>
        ) : dialItem?.phone ? (
          <OpenPhone.Provider value={openIncoming}>
            <NumberChat key={dialItem.key} item={dialItem} />
          </OpenPhone.Provider>
        ) : dialItem ? (
          <DialCard key={dialItem.key} item={dialItem} />
        ) : (
          <div className="grid flex-1 place-items-center">
            <span className="rounded-full bg-[var(--tg-service)] px-3 py-1 text-[15px] font-medium text-white">
              {dialOn ? "Выберите, кому звонить" : callsOn ? "Выберите звонок — откроется клиент" : "Выберите чат, чтобы начать переписку"}
            </span>
          </div>
        )}
      </section>
      {/* Серия из поиска без открытого чата — карточка без отметок; при открытом чате её рисует лента. */}
      {!chatId && Number(params.get("serial")) > 0 && (
        <SerialCardDialog serial={Number(params.get("serial"))} onClose={() => dropSerial(setParams)} />
      )}
      {/* «Чаты» вне каркаса кабинета, поэтому разметку экрана подключаем здесь сами (видна по праву `annotate`). */}
      <Annotator />
    </div>
  );
}

/**
 * Строка «найдено по номеру» из адреса: `phone-<номер>` — сам номер; `found-deal|lead-<id>` — по ответу
 * `GET /api/chats/number` (у объекта есть телефон). Без номера карточку не собрать — пусто.
 */
function restoredDial(key: string, found: NumberFound | undefined): DialItem | undefined {
  const phone = /^phone-(\d{11})$/.exec(key);
  if (phone) return phoneItem(phone[1]!);
  const hit = found?.items.find((i) => `found-${i.kind}-${i.id}` === key);
  if (!hit?.phone || (hit.kind !== "deal" && hit.kind !== "lead")) return undefined;
  const base = phoneItem(hit.phone);
  return hit.kind === "deal"
    ? { ...base, key, kind: "deal", id: hit.id, deal: { id: hit.id, stage: hit.sub, stage_code: "", kind: "other", date: null, rent_start: null, rent_end: null } }
    : { ...base, key, kind: "lead", id: hit.id, lead: { id: hit.id, status: hit.sub, status_code: "", active: true, date: null } };
}

/** С какого нашего номера писал этот сотрудник клиенту без переписки в прошлый раз — в браузере. */
const START_ACCOUNT = "prokat.chats.start-account";
const savedAccount = () => {
  try {
    return localStorage.getItem(START_ACCOUNT);
  } catch {
    return null;
  }
};

/**
 * Клиент, с которым переписки в «Чатах» нет (найден по номеру, строка «Обзвона» или «Звонков»), —
 * тем же окном, что любой чат (владелец 22.10.26: «единый интерфейс этого окна, даже когда диалогов
 * нет»): лента со звонками номера, панель сделки и поле ответа. Под ним — пустой чат WhatsApp
 * нашего номера из служебного браузера (`POST /api/chats/start`); в списке он появится с первым
 * сообщением. Пока переписки нет — выбор, с какого нашего номера писать; ответ уходит через мост.
 * Писать нельзя (ни один номер не отвечает из «Чатов», номер клиента не мобильный) — окно то же,
 * поле ответа закрыто с причиной (владелец 23.10.26). `DialCard` — только если номер не номер вовсе.
 * Спека `docs/specs/2026-10-22-chat-by-phone-design.md`.
 */
function NumberChat({ item }: { item: DialItem }) {
  const [account, setAccount] = useState<string | null>(savedAccount);
  const phone = item.phone ?? "";
  const started = useQuery({
    queryKey: ["chat-start", phone, account],
    queryFn: () => startChat(phone, account, item.deal?.id ?? null, item.key),
    staleTime: Infinity,
    retry: false,
  });
  // Запомненный номер больше не пишет (вкладка не на связи, ответ выключен) — берём первый доступный.
  const stale = started.error instanceof ApiFailure && started.error.code === "BAD_ACCOUNT" && account !== null;
  useEffect(() => {
    if (stale) setAccount(null);
  }, [stale]);
  const id = started.data?.chat_id ?? 0;
  // Та же лента, что грузит `Thread`, — из общего кэша, сами не запрашиваем.
  const feed = useQuery({ queryKey: ["client-chat", id], queryFn: () => loadChat(id), enabled: false });
  if (started.isPending || stale) {
    return (
      <div className="grid flex-1 place-items-center">
        <span className="rounded-full bg-[var(--tg-service)] px-3 py-1 text-[15px] font-medium text-white">Открываю чат…</span>
      </div>
    );
  }
  if (started.isError) {
    return (
      <div className="flex min-w-0 flex-1 flex-col">
        <p className="mx-auto w-full max-w-[728px] px-4 pt-2 text-[13px] text-[#e53935]" role="alert">
          Написать клиенту отсюда нельзя: {started.error.message}
        </p>
        <DialCard item={item} />
      </div>
    );
  }
  const { accounts } = started.data;
  const pick = (a: StartAccount) => {
    setAccount(a.account);
    try {
      localStorage.setItem(START_ACCOUNT, a.account);
    } catch {
      /* без браузерного хранилища — выбор живёт до перезагрузки */
    }
  };
  const empty = !feed.data?.messages.length;
  const top = empty && accounts.length > 0 && (
    <div className="flex flex-wrap items-center gap-1.5 px-3 pt-1.5 text-[13px] text-[var(--tg-sec)]" data-annot="писать с номера">
      <span>Переписки нет — написать с WhatsApp:</span>
      {accounts.map((a) => {
        const on = a.account === started.data.account;
        return (
          <button
            key={a.account}
            type="button"
            onClick={() => pick(a)}
            aria-pressed={on}
            title={`+${a.phone} — ответ уходит набором в служебном браузере (мост)`}
            className={`h-7 rounded-full px-3 font-medium ${on ? "bg-[var(--tg-primary)] text-white" : "bg-[var(--tg-input)] text-[var(--tg-text)] hover:bg-[var(--tg-hover)]"}`}
          >
            {a.title}
          </button>
        );
      })}
    </div>
  );
  return <Thread key={id} chatId={id} dial={item} top={top} />;
}

/** Плашка выборки по вопросу базы знаний: чаты и звонки, где его задавали, — переключатель между ними. */
function KbqBanner({ info, calls, onView, onDrop }: { info: KbqInfo | null; calls: boolean; onView: (calls: boolean) => void; onDrop: () => void }) {
  const tab = (on: boolean, label: string, n: number | undefined, toCalls: boolean) => (
    <button
      type="button"
      onClick={() => onView(toCalls)}
      className={`rounded-full px-2 py-0.5 text-[12px] ${on ? "bg-[var(--tg-primary)] text-white" : "text-[var(--tg-primary)] hover:bg-[var(--tg-hover)]"}`}
    >
      {label} · {n ?? "…"}
    </button>
  );
  return (
    <div
      className="mx-3 mb-2 flex flex-none items-center gap-2 rounded-[12px] bg-[var(--tg-primary)]/10 py-1.5 pl-3 pr-1.5 text-[13px] leading-[18px]"
      data-annot="выборка по вопросу базы знаний"
    >
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium" title={info?.text}>
          Вопрос: {info ? `«${info.text}»` : "…"}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-1 text-[var(--tg-sec)]">
          {tab(!calls, "Чаты", info?.chats, false)}
          {tab(calls, "Звонки", info?.calls, true)}
          {info && <span className="ml-1">{info.days ? `за ${info.days} дн.` : "за всё время"}</span>}
        </div>
      </div>
      <button
        type="button"
        onClick={onDrop}
        title="Показать все чаты"
        className="grid size-8 flex-none place-items-center rounded-full text-[var(--tg-sec)] hover:bg-[var(--tg-hover)]"
      >
        <X size={18} />
      </button>
    </div>
  );
}

function ChatRow({ chat, active, msg }: { chat: Chat; active: boolean; msg?: number | undefined }) {
  const on = active ? "bg-[var(--tg-primary)] text-white" : "hover:bg-[var(--tg-hover)]";
  const dim = active ? "text-white/80" : "text-[var(--tg-sec)]";
  const line = dealLine(chat);
  const { search } = useLocation(); // фильтр по тегам живёт в адресе — не теряем при открытии чата
  const p = new URLSearchParams(search);
  p.delete("dial"); // карточка найденного по номеру остаётся позади
  // Выборка по вопросу базы знаний: чат открывается на сообщении, где клиент его задал.
  if (msg) p.set("msg", String(msg));
  else p.delete("msg");
  return (
    <Link to={{ pathname: `/chats/${chat.id}`, search: p.toString() }} className={`flex h-[84px] items-center gap-3 rounded-[16px] px-2.5 ${on}`}>
      <Avatar chat={chat} size={54} />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="flex min-w-0 flex-1 items-baseline gap-1.5">
            <span className="min-w-0 truncate font-medium">{titleOf(chat)}</span>
            {chat.muted && (
              <span title="Без уведомлений" className={`flex-none self-center ${dim}`}>
                <BellOff size={14} />
              </span>
            )}
            <AccountPlace chat={chat} full={placeFull(chat)} compact inverse={active} className={`max-w-[60%] flex-none text-[12px] ${dim}`}>
              {placeOf(chat)}
            </AccountPlace>
          </span>
          {((chat.last.out && !chat.waits) || chat.answered) && (
            <span title={chat.waits ? "Отмечено: ответили" : undefined} className={`flex-none self-center ${active ? "" : "text-[var(--tg-primary)]"}`}>
              <Check size={16} />
            </span>
          )}
          {chat.suggest && (
            <span title="Ответ AI ждёт решения" className={`flex-none self-center ${active ? "" : "text-[var(--tg-primary)]"}`} data-annot="черновик готов">
              <Sparkles size={15} />
            </span>
          )}
          {(chat.tasks ?? 0) > 0 && (
            <span
              title={`Активных задач в салоне: ${chat.tasks} — переслано и ещё не отмечено завершённым`}
              className={`flex flex-none items-center gap-0.5 self-center text-[12px] ${active ? "" : "text-[#b26a00]"}`}
              data-annot="задача в салоне"
            >
              <ClipboardList size={15} />
              {(chat.tasks ?? 0) > 1 && chat.tasks}
            </span>
          )}
          <span className={`flex-none text-[12px] ${dim}`}>{listWhen(chat.last.date)}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className={`min-w-0 flex-1 truncate ${dim}`}>
            {chat.last.out && <span className={active ? "" : "text-[var(--tg-text)]"}>Вы: </span>}
            {preview(chat.last)}
          </span>
          {chat.unread > 0 && (
            <span
              className={`flex-none rounded-full px-[7px] text-[14px] font-medium leading-[22px] ${active ? "bg-white text-[var(--tg-primary)]" : chat.muted ? "bg-[var(--tg-sec)] text-white" : "bg-[var(--tg-primary)] text-white"}`}
            >
              {chat.unread}
            </span>
          )}
        </div>
        {/* Сделка диалога из кэша: стадия и дата записи / обработки, чтобы не открывать чат. */}
        <div className="flex items-center gap-1.5 text-[12px] leading-[18px]">
          <span className={`min-w-0 flex-1 truncate ${active ? "text-white/85" : line ? DEAL_COLOR[line.kind] : dim}`} data-annot="сделка диалога">
            {line ? line.text : " "}
          </span>
          {/* Теги чата: два первых, остальные числом — полный список в подсказке и в панели. */}
          {(chat.tags ?? []).length > 0 && (
            <span className="flex max-w-[55%] flex-none items-center gap-1" title={chat.tags.join(", ")} data-annot="теги чата в списке">
              {chat.tags.slice(0, 2).map((t) => (
                <span
                  key={t}
                  className={`min-w-0 truncate rounded-full px-1.5 text-[11px] leading-[16px] ${active ? "bg-white/25 text-white" : "bg-[var(--tg-input)] text-[var(--tg-sec)]"}`}
                >
                  {t}
                </span>
              ))}
              {chat.tags.length > 2 && <span className={`flex-none text-[11px] ${dim}`}>+{chat.tags.length - 2}</span>}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}

/**
 * Строка «Обзвона»: кто, номер и где с ним переписывались, лид или сделка со стадией и звонили ли
 * сегодня. Клик — последний чат клиента общей лентой всех каналов, без переписки — карточка с
 * историей (`components/chat/Dial`); в обоих «Звонить».
 */
function DialRow({ item, active, onPick }: { item: DialItem; active: boolean; onPick: (item: DialItem) => void }) {
  const { search } = useLocation();
  const p = new URLSearchParams(search);
  p.set("view", "dial");
  p.set("dial", item.key);
  const on = active ? "bg-[var(--tg-primary)] text-white" : "hover:bg-[var(--tg-hover)]";
  const dim = active ? "text-white/80" : "text-[var(--tg-sec)]";
  const line = dealLine(item);
  const obj = dialObject(item);
  const where = [...new Set(item.chats.map((c) => channelOf(c.channel).short))].join(", ");
  return (
    <Link
      to={{ pathname: item.chat ? `/chats/${item.chat.id}` : "/chats", search: p.toString() }}
      onClick={() => onPick(item)}
      className={`flex h-[84px] items-center gap-3 rounded-[16px] px-2.5 ${on}`}
      data-annot="строка обзвона"
    >
      <span
        title={
          item.missed
            ? "Пропущенный входящий — ждёт перезвона"
            : item.calls_today
              ? `Сегодня звонили ${item.calls_today}`
              : "Сегодня ещё не звонили"
        }
        className={`grid size-[54px] flex-none place-items-center rounded-full text-white ${item.missed ? "bg-[#e53935]" : item.calls_today ? "bg-[var(--tg-sec)]" : "bg-[#2e7d32]"}`}
      >
        {item.missed ? <PhoneMissed size={24} /> : <Phone size={24} />}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="min-w-0 truncate font-medium">{dialName(item)}</span>
          {/* Пропущенный без перезвона — красным (владелец 20.10.26); иначе — когда мы звонили последний
              раз: по нему порядок — не звонили сверху, сегодня — в конце. */}
          {item.missed ? (
            <span
              className={`min-w-0 truncate text-[12px] font-medium ${active ? "text-white" : "text-[#e53935]"}`}
              title={missedText(item, true)}
              data-annot="ждёт перезвона"
            >
              {missedText(item)}
            </span>
          ) : (
            <span
              className={`flex-none text-[12px] ${active ? "text-white/85" : item.last_out ? (item.calls_today ? "text-[var(--tg-sec)]" : "text-[#c98a00]") : "text-[#2e7d32]"}`}
              title={item.last_out ? `Последний наш исходящий ${whenShort(item.last_out)}` : "Наших исходящих за месяц не было"}
            >
              {item.last_out ? `звонили ${listWhen(item.last_out)}` : "не звонили"}
            </span>
          )}
          <span className="flex-1" />
          <span className={`flex-none text-[12px] ${dim}`} title="Создан">
            {listWhen(item.created)}
          </span>
        </div>
        <div className={`truncate ${dim}`}>
          {[
            // «Все» у владельца: на ком строка — первым, иначе срежется по ширине.
            item.owner ? `на: ${item.owner}` : "",
            dialPerson(item) ? (item.phone ? fmtPhone(item.phone) : "номера нет") : "",
            item.region ? regionText(item.region) : "",
            dialSource(item),
            where ? `переписка: ${where}` : "переписки нет",
          ]
            .filter(Boolean)
            .join(" · ")}
        </div>
        <div className="flex items-center gap-1.5 text-[12px] leading-[18px]">
          <span className={`min-w-0 flex-1 truncate ${active ? "text-white/85" : line ? DEAL_COLOR[line.kind] : dim}`}>
            {line ? line.text : !obj ? "в Bitrix не найден" : obj.kind === "lead" ? `Лид ${obj.id}` : `Сделка ${obj.id}`}
          </span>
          {/* Ждёт перезвона — когда последняя попытка после пропуска и сколько их из 3 (владелец 03.11.26). */}
          {item.missed ? (
            missedTryText(item) && (
              <span className={`flex-none ${dim}`} title="Последний наш исходящий после пропуска; с 3 попыток номер уходит из очереди">
                {missedTryText(item)}
              </span>
            )
          ) : item.calls_today > 0 && (
            <span className={`flex-none ${dim}`} title={item.last_call ? `Последний звонок ${whenShort(item.last_call)}` : undefined}>
              звонили {item.calls_today}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}

/**
 * Строка «Звонков»: кто, когда, входящий или исходящий и чем кончился, сделка или лид номера сейчас.
 * Клик — как у «Обзвона»: последний чат клиента общей лентой, без переписки — карточка с историей.
 */
function CallRow({ item, active, onPick }: { item: DialItem; active: boolean; onPick: (item: DialItem) => void }) {
  const { search } = useLocation();
  const call = item.call;
  if (!call) return null;
  const p = new URLSearchParams(search);
  p.set("view", "calls");
  p.set("dial", item.key);
  const on = active ? "bg-[var(--tg-primary)] text-white" : "hover:bg-[var(--tg-hover)]";
  const dim = active ? "text-white/80" : "text-[var(--tg-sec)]";
  const line = dealLine(item);
  const obj = dialObject(item);
  const where = [...new Set(item.chats.map((c) => channelOf(c.channel).short))].join(", ");
  const failed = call.result === "missed" || call.result === "noanswer";
  const Icon = call.result === "missed" ? PhoneMissed : call.direction === "in" ? PhoneIncoming : PhoneOutgoing;
  return (
    <Link
      to={{ pathname: item.chat ? `/chats/${item.chat.id}` : "/chats", search: p.toString() }}
      onClick={() => onPick(item)}
      className={`flex h-[84px] items-center gap-3 rounded-[16px] px-2.5 ${on}`}
      data-annot="строка звонка"
    >
      <span
        title={callText(call)}
        className={`grid size-[54px] flex-none place-items-center rounded-full text-white ${
          call.result === "missed" ? "bg-[#e53935]" : failed || call.result === "none" ? "bg-[var(--tg-sec)]" : "bg-[#2e7d32]"
        }`}
      >
        <Icon size={24} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="min-w-0 truncate font-medium">{dialName(item)}</span>
          <span className="flex-1" />
          <span className={`flex-none text-[12px] ${dim}`} title={whenShort(call.at)}>
            {listWhen(call.at)}
          </span>
        </div>
        <div className={`truncate text-[13px] ${active ? "text-white" : call.result === "missed" ? "text-[#e53935]" : ""}`}>
          {/* «Все»: кто разговаривал — первым, иначе срежется по ширине. */}
          {item.owner ? `${item.owner} · ${callText(call)}` : callText(call)}
        </div>
        <div className="flex items-center gap-1.5 text-[12px] leading-[18px]">
          <span className={`min-w-0 flex-1 truncate ${active ? "text-white/85" : line ? DEAL_COLOR[line.kind] : dim}`}>
            {[
              line ? line.text : !obj ? "в Bitrix не найден" : obj.kind === "lead" ? `Лид ${obj.id}` : `Сделка ${obj.id}`,
              dialPerson(item) && item.phone ? fmtPhone(item.phone) : "",
              where ? `переписка: ${where}` : "",
            ]
              .filter(Boolean)
              .join(" · ")}
          </span>
        </div>
      </div>
    </Link>
  );
}

/** Связанный чат клиента под выбранным: строка ниже и уже обычной, клик — переход в него. */
function RelatedRow({ chat, active = false }: { chat: ChatBrief; active?: boolean }) {
  const { search } = useLocation();
  return (
    <Link
      to={{ pathname: `/chats/${chat.id}`, search }}
      title={`${placeFull(chat)} · ${titleOf(chat)}`}
      aria-current={active ? "page" : undefined}
      className={`flex h-[52px] items-center gap-2.5 rounded-[12px] px-2 ${active ? "bg-[var(--tg-hover)] ring-1 ring-[var(--tg-primary)]/50" : "hover:bg-[var(--tg-hover)]"}`}
    >
      <Avatar chat={chat} size={36} />
      <div className="min-w-0 flex-1 text-[13px] leading-[18px]">
        <div className="flex items-baseline gap-2">
          <span className="min-w-0 flex-1 truncate">
            <AccountPlace chat={chat} full={placeFull(chat)} compact className="font-medium">
              {placeOf(chat)}
            </AccountPlace>
            <span className="text-[var(--tg-sec)]"> · {titleOf(chat)}</span>
          </span>
          <span className="flex-none text-[12px] text-[var(--tg-sec)]">{listWhen(chat.last.date)}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="min-w-0 flex-1 truncate text-[var(--tg-sec)]">
            {chat.last.out && <span className="text-[var(--tg-text)]">Вы: </span>}
            {preview(chat.last)}
          </span>
          {chat.unread > 0 && (
            <span className="flex-none rounded-full bg-[var(--tg-primary)] px-1.5 text-[12px] font-medium leading-[18px] text-white">
              {chat.unread}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}

/**
 * Переключатель в шапке чата: «Все» — переписка клиента одной лентой, дальше его чаты по месту,
 * где общались («WA +7 925 605-23-69», «TG-бот»); текущий выделен, порядок — по id, не прыгает.
 */
function ChatSwitch({
  chat,
  all,
  onAll,
  onChat,
}: {
  chat: Chat;
  all: boolean;
  onAll: (on: boolean) => void;
  /** Лента встроена в чужой экран: другой чат клиента открывает он, адрес «Чатов» не трогаем. */
  onChat?: ((id: number) => void) | undefined;
}) {
  const { search } = useLocation();
  const chats = [chat, ...chat.related].sort((a, b) => a.id - b.id);
  const pill = "flex h-8 min-w-0 flex-none items-center gap-1.5 rounded-full px-3 text-[13px] font-medium shadow-[0_1px_3px_rgba(0,0,0,.16)]";
  const tone = (on: boolean) =>
    on ? "bg-[var(--tg-primary)] text-white" : "bg-[var(--tg-surface)] text-[var(--tg-text)] hover:bg-[var(--tg-hover)]";
  return (
    <nav className="mt-1.5 flex gap-1.5 overflow-x-auto" data-annot="переключатель чатов клиента">
      <button
        type="button"
        onClick={() => onAll(true)}
        title="Вся переписка клиента одной лентой — по всем каналам"
        aria-pressed={all}
        className={`${pill} ${tone(all)}`}
      >
        Все
      </button>
      {chats.map((c) => {
        const on = !all && c.id === chat.id;
        return (
          <Link
            key={c.id}
            to={{ pathname: `/chats/${c.id}`, search }}
            onClick={(e) => {
              onAll(false);
              if (onChat) {
                e.preventDefault();
                onChat(c.id);
              }
            }}
            title={`${placeFull(c)} · ${titleOf(c)}${c.last.date ? ` · ${listWhen(c.last.date)}` : ""}`}
            aria-current={on ? "page" : undefined}
            className={`${pill} ${tone(on)} ${all && c.id === chat.id ? "ring-1 ring-[var(--tg-primary)]" : ""}`}
          >
            <span className="size-2.5 flex-none rounded-full ring-1 ring-white" style={{ background: channelOf(c.channel).color }} />
            <span className="max-w-[200px] truncate">{placeOf(c)}</span>
            {!on && c.unread > 0 && (
              <span className="flex-none rounded-full bg-[var(--tg-primary)] px-1.5 text-[12px] leading-[18px] text-white">
                {c.unread}
              </span>
            )}
          </Link>
        );
      })}
    </nav>
  );
}

/**
 * Лента чата с полем ответа. `onChat` — лента встроена в панель другого экрана («Доставки»):
 * переход между чатами клиента отдаём ему, своей панели сделки и кнопки «к чатам» не рисуем —
 * сделку рядом показывает сам экран. Обёртка — класс `tgc` (палитра) и высота.
 */
export function Thread({
  chatId,
  onChat,
  dial,
  allFirst = false,
  top,
}: {
  chatId: number;
  onChat?: (id: number) => void;
  /** Открыт из «Обзвона»: в шапке «Звонить», лента сразу по всем каналам клиента. */
  dial?: DialItem | undefined;
  /** Открыт поиском по номеру: лента сразу «Все» — по всем каналам клиента. */
  allFirst?: boolean;
  /** Строка под шапкой — у чата по номеру без переписки: с какого нашего номера писать. */
  top?: ReactNode;
}) {
  const embedded = !!onChat;
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { search } = useLocation();
  const { data: me } = useGetIdentity<CurrentUser>();
  // Ответ, который уходит прямо сейчас: его пузырь стоит в ленте с первой секунды, раньше ответа
  // сервера. `seen` — сервер уже показал его своей строкой (набор в служебном браузере), дальше
  // свою не рисуем.
  const [pending, setPending] = useState<{ text: string; at: string; seen: boolean } | null>(null);
  // Открыли диалог — мост освежит его галки и историю (MAX/Telegram служебных браузеров).
  useEffect(() => {
    refreshChat(chatId).catch(() => {});
  }, [chatId]);
  const q = useQuery({
    queryKey: ["client-chat", chatId],
    queryFn: () => loadChat(chatId),
    // Пока ответ набирается в служебном браузере, шаги набора читаем чаще.
    refetchInterval: (query) => (pending && query.state.data?.chat.context?.web ? 800 : 4000),
  });
  const [text, setText] = useState("");
  // Файл ушёл, а подпись нет: сказать сотруднику и вернуть подпись в поле — отправить её текстом.
  const [sendWarning, setSendWarning] = useState<string | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [viewer, setViewer] = useState<ChatFile | null>(null);
  // «Все» в переключателе: переписка клиента одной лентой по всем его чатам. Чат открывается
  // своей лентой (`Thread` пересоздаётся на каждый чат), общую включают руками; из «Обзвона» —
  // сразу общая: перед звонком нужно видеть всё, о чём с клиентом говорили; из поиска по номеру — тоже.
  const [allOn, setAll] = useState(!!dial || allFirst);
  // Поиск похожих платьев по фото клиента: сообщение с фото (в общей ленте — из любого чата клиента).
  // Фото для «Найти похожие»: сообщение чата или (`id: null`) объявление Авито.
  const [dress, setDress] = useState<{ id: number | null; chat_id: number } | null>(null);
  // Сообщение, которое пересылают в чат салона.
  // Переслать в салон одно сообщение (стрелка у пузыря); пачку пересылает плашка выделения `PickBar`.
  const [forward, setForward] = useState<{ chatId: number; ids: number[] } | null>(null);
  // Ответ на конкретное сообщение (реплай, как в Telegram): полоска над полем, уходит со следующей отправкой.
  const [replyTo, setReplyTo] = useState<ChatMessage | null>(null);
  // Что уйдёт по «Отправить»: сообщение клиенту (по умолчанию), комментарий к сделке CRM или вопрос
  // в группу салона города; у каждого чата — заново «Сообщение» (`Thread` пересоздаётся на чат).
  const [mode, setMode] = useState<ComposeMode>("message");
  const [salonPick, setSalonPick] = useState<number | null>(null);
  const salonForm = useQuery(salonQuery(chatId, mode === "salon"));
  const salonCity = salonPick ?? salonForm.data?.city ?? null;
  // Каталог платьев с фильтром: фото отмеченных — в поле ответа.
  const [catalog, setCatalog] = useState(false);
  const [drag, setDrag] = useState(false);
  // Сделка справа: на широком экране открыта сразу, на узком — по кнопке поверх ленты или по ссылке
  // на вкладку (`?panel=qc` из бейджа или чужой ссылки).
  const [panel, setPanel] = useState(
    () => !embedded && (window.innerWidth >= 1280 || new URLSearchParams(window.location.search).has("panel")),
  );
  // «Кто видит группу» — по шестерёнке у названия группы, по умолчанию закрыта (владелец 21.10.26:
  // «чтобы не путать»); шестерёнка — только с правом `chat_groups_edit`.
  const [groupSettings, setGroupSettings] = useState(false);
  // Замечания контроля качества: в чате клиента — вкладка «Замечания» панели, в группе — своя
  // панель справа (панели сделки там нет); `qcFocus` — замечание, по бейджу которого пришли.
  const [qcOpen, setQcOpen] = useState(false);
  const [qcFocus, setQcFocus] = useState<number | null>(null);
  const canQc = useCan(QC_REMARK);
  const canKb = useCan(KNOWLEDGE_EDIT);
  const canChats = useCan(CLIENT_CHATS);
  const qcSee = (canQc || canKb) && canChats;
  const canGroupEdit = useCan(CHAT_GROUPS_EDIT);
  useEffect(() => setGroupSettings(false), [chatId]);
  // Кнопка AI: черновик по сценарию типа обращения — только по нажатию (Thread смонтирован по chatId).
  const ai = useChatAi(chatId);
  // Вкладки панели — те же и в том же порядке, что в панели расширения Chrome: «Сделка» · «История»
  // · третья (там «Товар», здесь «Клиент» — чем интересовался); здесь же «Договор» (если есть
  // сделка) и «ЛК»; в адресе `?panel=contract|history|client|lk`.
  const [params, setParams] = useSearchParams();
  const asked = params.get("panel");
  const [hasDeal, setHasDeal] = useState(false);
  const canSuggest = useCan(CHAT_SUGGEST);
  const tabs = PANEL_TABS.filter(
    (t) => (t.id !== "contract" || hasDeal) && (t.id !== "qc" || qcSee) && (t.id !== "ai" || canSuggest),
  );
  const side: PanelSide = tabs.find((t) => t.id !== "deal" && t.id === asked)?.id ?? "deal";
  const setSide = (next: PanelSide) =>
    setParams(
      (had) => {
        const p = new URLSearchParams(had);
        if (next !== "deal") p.set("panel", next);
        else p.delete("panel");
        return p;
      },
      { replace: true },
    );
  const openQcNow = useRef((_?: number) => {});
  openQcNow.current = (id?: number) => {
    setQcFocus(id ?? null);
    if (chat?.group) return setQcOpen(true);
    setPanel(true);
    setSide("qc");
  };
  // Стабильная ссылка для `QcProvider`: контекст ленты не пересобирается на каждый ввод.
  const openQc = useCallback((id?: number) => openQcNow.current(id), []);
  // Данные «Клиента» (0,5 с) — только когда вкладку открыли; число на ней — после первого захода.
  const client = useQuery({ ...clientQuery(chatId), enabled: panel && side === "client" });
  const traces = clientTraces(client.data);
  const bottom = useRef<HTMLDivElement>(null);
  const feedBox = useRef<HTMLDivElement>(null);
  const field = useRef<HTMLTextAreaElement>(null);
  const picker = useRef<HTMLInputElement>(null);
  const send = useMutation({
    // Файлы уходят по одному; текст поля — подписью к первому (как в Telegram).
    // Ответ на сообщение — у текста или у первого файла (у него и подпись).
    mutationFn: async ({ value, list, reply }: { value: string; list: File[]; reply: ChatMessage | null }) => {
      setSendWarning(null);
      if (list.length === 0) {
        // Чат по номеру (`NumberChat`): первый ответ мог уйти в диалог, который мост принёс раньше.
        const sent = await sendToChat(chatId, value, reply?.id);
        if (sent.chat_id && sent.chat_id !== chatId && !embedded) {
          const p = new URLSearchParams(search);
          p.delete("dial");
          void navigate({ pathname: `/chats/${sent.chat_id}`, search: p.toString() });
        }
        return;
      }
      for (const [i, file] of list.entries()) {
        const sent = await sendFileToChat(chatId, file, i === 0 ? value : "", i === 0 ? reply?.id : null);
        setFiles((rest) => rest.filter((f) => f !== file));
        if (i === 0) setText(sent.warning && value ? value : "");
        if (sent.warning) setSendWarning(sent.warning);
      }
    },
    // Текст убран из поля в момент отправки. Не ушло — возвращаем, чтобы не набирать заново; «не
    // подтверждено» не возвращаем: сообщение могло уйти, повтор с ходу был бы вторым клиенту.
    onError: (failure, { value, list, reply }) => {
      const unsure = failure instanceof ApiFailure && failure.code === "SEND_UNKNOWN";
      if (list.length === 0 && !unsure) setText((had) => had || value);
      if (!unsure && reply) setReplyTo((had) => had ?? reply);
    },
    onSettled: async () => {
      void qc.invalidateQueries({ queryKey: ["client-chats"] });
      void qc.invalidateQueries({ queryKey: ["waiting"] });
      void qc.invalidateQueries({ queryKey: ["client-chat", chatId] });
      // Свой пузырь «отправляется» держим, пока лента не перечитана: иначе он мигнёт пустотой.
      // Ждём только саму ленту — запросы панели под тем же ключом бывают долгими.
      await qc.refetchQueries({ queryKey: ["client-chat", chatId], exact: true });
      setPending(null);
    },
  });
  // Комментарий к сделке или вопрос в салон: не сообщение клиенту — пузыря «отправляется» нет,
  // заметка появляется в ленте после перечитывания (сервер сразу кладёт её в свою копию).
  const note = useMutation({
    mutationFn: ({ value, kind, city }: { value: string; kind: ComposeMode; city: number | null }) =>
      kind === "salon" ? sendSalonQuestion(chatId, { text: value, city: city! }) : sendComment(chatId, value),
    onSuccess: () => setText(""),
    // Комментарий уже записан, не ушёл только вопрос в группу — повтор из поля записал бы второй.
    onError: (failure) => failure instanceof ApiFailure && failure.code === "PODBOR_FAILED" && setText(""),
    onSettled: (_, __, { kind }) => {
      void qc.invalidateQueries({ queryKey: ["client-chat", chatId] });
      void qc.invalidateQueries({ queryKey: ["chat-actions", chatId] });
      if (kind === "salon") {
        void qc.invalidateQueries({ queryKey: ["chat-podbor", chatId] });
        void qc.invalidateQueries({ queryKey: ["client-chats"] });
      }
    },
  });
  // «Ответили» — отметка руками, когда ответ ушёл не отсюда (звонок, Bitrix): чат уходит из «Без ответа».
  const answered = useMutation({
    mutationFn: () => markAnswered(chatId),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["client-chat", chatId] });
      void qc.invalidateQueries({ queryKey: ["client-chats"] });
      void qc.invalidateQueries({ queryKey: ["waiting"] });
    },
  });
  // «Без уведомлений» у группы — только для меня, как в Telegram: число на «Группах» её не считает.
  const mute = useMutation({
    mutationFn: (on: boolean) => muteGroup(chatId, on),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["client-chat", chatId] });
      void qc.invalidateQueries({ queryKey: ["client-chats"] });
    },
  });
  const addFiles = (list: FileList | File[] | null) => {
    if (list && list.length > 0) setFiles((had) => [...had, ...Array.from(list)]);
  };
  const tooBig = files.find((f) => f.size > MAX_SEND);
  const chat = q.data?.chat;
  // Окно 24 ч WhatsApp закрыто — то, что сервер наверняка отобьёт (502 `SEND_FAILED`), не отправляем.
  const waProblem = chat?.whatsapp && !chat.whatsapp.open && mode === "message" ? waClosedProblem(text, files.length) : "";
  // «Звонить» в шапке — номер строки «Обзвона», иначе активный номер чата; у группы звонить некому.
  const callPhone = dial?.phone || (chat && !chat.group ? chat.phone : null);
  const all = allOn && (chat?.related ?? []).length > 0;
  // Ключ начинается как у чата: отправка и правки в панели обновляют и общую ленту.
  const history = useQuery({
    queryKey: ["client-chat", chatId, "all"],
    queryFn: () => loadChatHistory(chatId),
    enabled: all,
    refetchInterval: 4000,
  });
  const feed = all ? history : q;
  const loaded = feed.data?.messages ?? [];
  // Ступень предупреждения над полем: шаблоны подряд молчащему клиенту WhatsApp отклоняет.
  const waNote = chat && mode === "message" ? waStage(chat, loaded) : null;
  // Сервер показывает набор в служебном браузере своей строкой — тогда она и есть пузырь ответа.
  const onServer = !!pending && loaded.some((m) => m.status === "sending" && m.text === pending.text);
  useEffect(() => {
    if (onServer) setPending((p) => (p && !p.seen ? { ...p, seen: true } : p));
  }, [onServer]);
  const messages: ChatMessage[] =
    pending && !pending.seen && !onServer
      ? [...loaded, { id: 0, chat_id: chatId, out: true, date: pending.at, text: pending.text, author: "", auto: false, file: null, status: "sending", step: null }]
      : loaded;
  const byId = new Map((chat ? [chat, ...chat.related] : []).map((c) => [c.id, c]));
  // Звонки клиента — в той же хронологии: просьба КЦ «объединить всё в одном месте».
  const calls = feed.data?.calls ?? [];
  // Комментарии сотрудников из CRM — тоже в хронологии; старый сервер поля не отдаёт.
  const comments = feed.data?.comments ?? [];
  // Приходы в салон — карточкой; комментарии прихода, у которого есть карточка, — внутри неё.
  const visits = feed.data?.visits ?? [];
  const visitIds = new Set(visits.map((v) => v.id));
  const ofVisit = (c: ChatComment) => (c.kind === "visit" && c.visit && visitIds.has(c.visit.id) ? c.visit.id : null);
  // В группе все сообщения «наши» (`out`), и сторону решаем сами, как в Telegram: справа — мои и
  // самого бота, слева — остальные с именем (владелец 21.10.26: «разделить как диалог»).
  const sided = chat?.group
    ? messages.map((m) => ({ ...m, out: m.id <= 0 || (!!me && m.user_id === me.id) || !!m.bot }))
    : messages;
  // Уведомления, ушедшие по сделке в группы салона (`chat/notices.py`), — плашкой в той же хронологии.
  const notices = feed.data?.notices ?? [];
  // Лиды номеров (`chat/leads.py`): что сделал клиент — виджет, звонок — и какая рассылка ушла в ответ.
  const leads = feed.data?.leads ?? [];
  // Треки посылок сделки из CRM — плашкой по дате создания; клик открывает трек в «Доставке».
  const trackDeal = chat && !chat.group ? (chat.deal?.id ?? null) : null;
  const tracksQ = useQuery({
    queryKey: dealTracksKey(trackDeal ?? 0),
    queryFn: () => loadDealTracks(trackDeal!),
    enabled: !!trackDeal,
    staleTime: 60_000,
  });
  const openTrack = (id: number) => {
    setPanel(true);
    setParams(
      (had) => {
        const p = new URLSearchParams(had);
        p.set("panel", "delivery");
        p.set("dsub", "track");
        p.set("track", String(id));
        return p;
      },
      { replace: true },
    );
  };
  const lenta = merge(
    sided,
    calls,
    comments.filter((c) => ofVisit(c) === null),
    visits,
    notices,
    trackDeal ? (tracksQ.data?.tracks ?? []) : [],
    leads,
  );
  const count = messages.length;
  // Пузырь «отправляется» растёт, когда появляется шаг набора, — лента должна остаться у края.
  const tail = messages[count - 1];
  const sendingStep = tail?.status === "sending" ? (tail.step ?? "") : null;
  // Новое сообщение — лента у нижнего края, как в Telegram.
  // В фигурных скобках: `scrollIntoView` в новом Chrome отдаёт Promise, и React принял бы его за очистку.
  useLayoutEffect(() => {
    bottom.current?.scrollIntoView({ block: "end" });
  }, [count, sendingStep]);
  // `?msg=<id>` — открыть чат на сообщении (из плашки уведомления: «Открыть в группе»): прокрутить
  // и подсветить один раз, когда оно появилось в ленте; потом лента живёт как обычно. Лента чата со
  // связанными сменяется общей «Все» уже после первого перехода и уводит вниз — в ключе и она; прыжок
  // мгновенный: плавный перебивает «стояла у нижнего края — там и остаётся» ниже.
  const jumpMsg = Number(params.get("msg")) || null;
  const jumped = useRef<string | null>(null);
  useEffect(() => {
    const key = `${chatId}:${jumpMsg}:${all}`;
    if (!jumpMsg || jumped.current === key || !loaded.some((m) => m.id === jumpMsg)) return;
    jumped.current = key;
    window.setTimeout(() => jumpTo(jumpMsg, "instant"), 50);
  }, [jumpMsg, chatId, loaded, all]);
  // Под лентой появляются карточка ответа AI и полосы запросов в салон — лента становится ниже.
  // Стояла у нижнего края — там и остаётся, иначе последнее сообщение клиента уходит под них.
  useEffect(() => {
    const box = feedBox.current;
    if (!box) return;
    let was = box.clientHeight;
    const watch = new ResizeObserver(() => {
      const now = box.clientHeight;
      if (now < was && box.scrollHeight - box.scrollTop - was < 80) box.scrollTop = box.scrollHeight;
      was = now;
    });
    watch.observe(box);
    return () => watch.disconnect();
  }, [chatId]);
  // Поле растёт с текстом до ~8 строк.
  useEffect(() => {
    const el = field.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 190)}px`;
  }, [text]);

  const noting = mode !== "message";
  const submit = () => {
    const value = text.trim();
    if (noting) {
      if (!value || note.isPending || (mode === "salon" && salonCity === null)) return;
      note.mutate({ value, kind: mode, city: salonCity });
      return;
    }
    if (!(value || files.length > 0) || tooBig || waProblem || send.isPending) return;
    if (files.length === 0) {
      // Как в мессенджере: текст сразу уходит из поля в ленту пузырём «отправляется».
      setPending({ text: value, at: localIso(), seen: false });
      setText("");
    }
    send.mutate({ value, list: files, reply: replyTo });
    setReplyTo(null);
  };
  // Ответить можно на сообщение этого чата (в общей ленте ответ уходит сюда же), где площадка
  // принимает реплай и поле ответа открыто.
  const canReply =
    !!chat?.reply && (!chat.group || !!chat.group_send) && !(chat.context?.web && !chat.web_send?.ok);
  const startReply = (m: ChatMessage) => {
    setReplyTo(m);
    field.current?.focus();
  };
  const column = "mx-auto w-full max-w-[728px] px-2 md:px-4";
  // «Сообщение · Комментарий · В салон · Замечание» — под полем ответа и под полем замечания.
  const modeBar = chat && !chat.group && (
    <ComposeModeBar chatId={chatId} mode={mode} onMode={setMode} deal={chat.deal?.id ?? null} city={salonPick} onCity={setSalonPick} />
  );

  return (
    <QcProvider chatId={chatId} onOpen={openQc}>
      <div className="flex min-w-0 flex-1 flex-col">
      <div className={`${column} flex-none`}>
        <header className="flex h-14 items-center gap-2 bg-[var(--tg-surface)] px-2 shadow-[0_1px_5px_-1px_rgba(0,0,0,.21)] max-md:-mx-2 md:h-12 md:rounded-[24px]">
          <button
            type="button"
            onClick={() => {
              // Карточку «Обзвона» или номера (`?dial=`) за спиной не оставляем — иначе «назад» вернул бы в неё же.
              const p = new URLSearchParams(search);
              p.delete("dial");
              void navigate({ pathname: "/chats", search: p.toString() });
            }}
            title="К чатам"
            hidden={embedded}
            className="grid size-10 flex-none place-items-center rounded-full text-[var(--tg-sec)] hover:bg-[var(--tg-hover)] md:hidden"
          >
            <ArrowLeft size={22} />
          </button>
          {!embedded && <ChatTrail chatId={chatId} title={chat ? titleOf(chat) : ""} />}
          {chat && <Avatar chat={chat} size={40} />}
          <div className="min-w-0 flex-1 leading-tight">
            <div className="flex min-w-0 items-center gap-1">
              <span className="truncate font-medium">{chat ? titleOf(chat) : "…"}</span>
              {chat?.group && (
                <button
                  type="button"
                  onClick={() => mute.mutate(!chat.muted)}
                  disabled={mute.isPending}
                  title={chat.muted ? "Без уведомлений — включить уведомления" : "Не уведомлять: группа не пойдёт в число на «Группах»"}
                  aria-pressed={!!chat.muted}
                  className="grid size-6 flex-none place-items-center rounded-full text-[var(--tg-sec)] hover:bg-[var(--tg-hover)] disabled:opacity-50"
                  data-annot="уведомления группы"
                >
                  {chat.muted ? <BellOff size={16} /> : <Bell size={16} />}
                </button>
              )}
              {chat?.group && canGroupEdit && (
                <button
                  type="button"
                  onClick={() => setGroupSettings(!groupSettings)}
                  title="Кто видит группу"
                  aria-pressed={groupSettings}
                  className={`grid size-6 flex-none place-items-center rounded-full hover:bg-[var(--tg-hover)] ${groupSettings ? "text-[var(--tg-primary)]" : "text-[var(--tg-sec)]"}`}
                  data-annot="настройка группы"
                >
                  <Settings size={16} />
                </button>
              )}
            </div>
            {chat && (
              <div className="truncate text-[14px] text-[var(--tg-sec)]">
                <AccountPlace chat={chat} full={placeFull(chat)}>
                  {placeHead(chat)}
                </AccountPlace>
                {chat.username && ` · @${chat.username}`}
                {/* Объявление Авито — полоской под шапкой (`AvitoItem`), здесь только город. */}
                {chat.context?.item && chat.context.city && ` · ${chat.context.city}`}
                {chat.channel === "livechat" && chat.context?.page && (
                  <>
                    {" · "}
                    <a href={chat.context.page} target="_blank" rel="noreferrer" className="hover:underline">
                      {new URL(chat.context.page).pathname.replace(/\/$/, "") || "главная"}
                    </a>
                  </>
                )}
                {chat.channel === "livechat" && chat.context?.open === false && " · диалог на сайте закрыт"}
                {chat.deals.length > 0 && ` · сделка ${chat.deals[0]}`}
                {chat.deals.length === 0 && chat.leads.length > 0 && ` · лид ${chat.leads[0]}`}
              </div>
            )}
          </div>
          {/* Вне «Обзвона» — только трубка: средняя колонка узкая, место в шапке нужно имени клиента. */}
          {chat?.group && <QcHeaderButton />}
          {chat && !chat.group && <PickHeaderButton />}
          {callPhone && <CallButton phone={callPhone} compact={!dial?.phone} />}
          <button
            type="button"
            onClick={() => setPanel(!panel)}
            title={panel ? "Скрыть сделку" : "Показать сделку"}
            hidden={embedded || !!chat?.group}
            className={`grid size-10 flex-none place-items-center rounded-full hover:bg-[var(--tg-hover)] ${panel ? "text-[var(--tg-primary)]" : "text-[var(--tg-sec)]"}`}
          >
            <PanelRight size={22} />
          </button>
        </header>
        {/* Клиент писал в нескольких каналах — переключатель между его чатами. */}
        {chat && (chat.related ?? []).length > 0 && <ChatSwitch chat={chat} all={all} onAll={setAll} onChat={onChat} />}
        {dial && (
          <div className="truncate px-3 pt-1.5 text-[13px] text-[var(--tg-sec)]" data-annot="обзвон в чате">
            {dial.call ? `Звонок ${listWhen(dial.call.at)}` : dial.found ? "По номеру" : "Обзвон"}: {dialWhat(dial)}
            {dial.phone && ` · ${fmtPhone(dial.phone)}`}
            {dial.missed && <span className="font-medium text-[#e53935]"> · {missedText(dial, true)}</span>}
          </div>
        )}
        {chat?.channel === "avito" && <AvitoItem chatId={chat.id} onSimilar={() => setDress({ id: null, chat_id: chat.id })} />}
        {top}
      </div>

      <div ref={feedBox} className="min-h-0 flex-1 overflow-y-auto">
        <div className={`${column} flex min-h-full flex-col justify-end gap-1 py-2`}>
          {feed.isPending && <Service>Загрузка…</Service>}
          {feed.isError && <Service>Не удалось загрузить переписку</Service>}
          {feed.data && lenta.length === 0 && <Service>Переписки с клиентом ещё нет</Service>}
          {lenta.map((it) =>
            "call" in it ? (
              <Fragment key={`call-${it.call.id}`}>
                {it.day && <Service>{dayLabel(it.call.date)}</Service>}
                <QcRow r={{ kind: "call", id: it.call.id, chat_id: chatId }} out={it.call.out} anchor={`qc-call-${it.call.id}`} menu>
                  <CallBubble c={it.call} />
                </QcRow>
              </Fragment>
            ) : "comments" in it ? (
              <Fragment key={`cmt-${it.comments[0].id}`}>
                {it.day && <Service>{dayLabel(it.comments[0].date)}</Service>}
                <CommentBubble comments={it.comments} feed={comments} />
              </Fragment>
            ) : "notice" in it ? (
              <Fragment key={`notice-${it.notice.kind}-${it.notice.id}`}>
                {it.day && <Service>{dayLabel(it.notice.date)}</Service>}
                <NoticeBubble n={it.notice} />
              </Fragment>
            ) : "lead" in it ? (
              <Fragment key={`lead-${it.lead.id}`}>
                {it.day && <Service>{dayLabel(it.lead.date)}</Service>}
                <LeadBubble l={it.lead} />
              </Fragment>
            ) : "track" in it ? (
              <Fragment key={`track-${it.track.id}`}>
                {it.day && <Service>{dayLabel(it.at)}</Service>}
                <TrackBubble t={it.track} onOpen={() => openTrack(it.track.id)} />
              </Fragment>
            ) : "visit" in it ? (
              <Fragment key={`visit-${it.visit.id}`}>
                {it.day && <Service>{dayLabel(it.visit.date)}</Service>}
                <VisitCard
                  v={it.visit}
                  comments={comments.filter((c) => ofVisit(c) === it.visit.id)}
                  feed={comments}
                  onOffer={(_, list) => {
                    // Похожие на сердечко примерки: фото с номером серии — к файлам ответа, отправляет сотрудник.
                    addFiles(list);
                    field.current?.focus();
                  }}
                />
              </Fragment>
            ) : (
              <QcRow
                key={it.m.id}
                r={it.m.id > 0 && it.m.status !== "sending" ? { kind: "message", id: it.m.id, chat_id: it.m.chat_id } : null}
                out={it.m.out}
              >
              <Bubble
                m={it.m}
                prev={it.prev}
                next={it.next}
                newDay={it.day}
                from={all ? byId.get(it.m.chat_id) : undefined}
                group={!!chat?.group}
                onOpen={setViewer}
                onDress={setDress}
                onForward={(m) => setForward({ chatId: m.chat_id, ids: [m.id] })}
                onReply={canReply && it.m.chat_id === chatId ? startReply : undefined}
              />
              </QcRow>
            ),
          )}
          <div ref={bottom} />
        </div>
      </div>

      <QcComposeSwitch
        className={`${column} flex-none pb-2 md:pb-3`}
        bar={modeBar}
        pickBar={<PickBar messages={messages} />}
      >
      <div
        className={`${column} flex flex-none flex-col pb-2 md:pb-3`}
        onDragOver={(e) => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);
          addFiles(e.dataTransfer.files);
        }}
      >
        {/* Окно 24 ч WhatsApp закрыто: ступень по нашим шаблонам за сутки — WaWindow.tsx. */}
        {waNote && chat && <WaWindowNote chat={chat} stage={waNote} place={placeOf} onChat={onChat} />}
        {chat?.vk_closed && (
          <p className="mb-1 px-3 text-[13px] text-red-500" data-annot="клиент закрыл сообщения">
            Не отправить: {chat.vk_closed}. Ответить можно, когда клиент сам напишет снова.
          </p>
        )}
        {all && chat && !chat.context?.web && (
          <p className="mb-1 px-3 text-[13px] text-[var(--tg-sec)]" data-annot="куда уйдёт ответ">
            Лента всех каналов клиента. Ответ уйдёт в <span className="font-medium text-[var(--tg-text)]">{placeOf(chat)}</span>
          </p>
        )}
        {/* Попытки дозвона и автоотмена лида Резервом: лид «Новый»/«В работе», сделка «В работе». */}
        {chat && !all && !chat.group && (chat.deal ? chat.deal.stage_code === "3" : ["NEW", "IN_PROCESS"].includes(chat.lead?.status_code ?? "")) && (
          <CallAttempts kind={chat.deal ? "deal" : "lead"} id={chat.deal?.id ?? chat.lead!.id} />
        )}
        {chat && waitsReply(chat) && (
          <div className="mb-1.5 flex items-center gap-2 px-3 text-[13px] text-[var(--tg-sec)]">
            <span>Клиент ждёт ответа</span>
            <button
              type="button"
              onClick={() => answered.mutate()}
              disabled={answered.isPending}
              title="Отметить, что ответили не отсюда — звонком или из Bitrix"
              className="ml-auto flex items-center gap-1 rounded-full bg-[var(--tg-surface)] px-3 py-1 font-medium text-[var(--tg-primary)] shadow-[var(--tg-shadow)] hover:bg-[var(--tg-hover)] disabled:opacity-50"
            >
              <Check size={16} /> Ответили
            </button>
          </div>
        )}
        {chat && chat.waits && chat.answered && (
          <p className="mb-1 px-3 text-[13px] text-[var(--tg-sec)]">
            Отмечено: ответили {dayLabel(chat.answered).toLowerCase()} в {hhmm(chat.answered)}
          </p>
        )}
        {/* Запросы на подбор платья в группу салона по фото клиента и ответы сотрудников. */}
        {chat && !all && <ChatPodbor chatId={chatId} onAttach={(file) => addFiles([file])} />}
        {/* Черновик ответа ИИ на последнее сообщение клиента: отправить, поправить или отклонить. */}
        {chat && !all && (
          <ChatSuggestCard
            chatId={chatId}
            waits={(chat.waits && !chat.answered) || ai.isSuccess}
            ai={ai}
            onHistory={() => {
              setSide("ai");
              setPanel(true);
            }}
            onEdit={(value) => {
              setText((had) => (had.trim() ? `${had.trimEnd()}\n${value}` : value));
              field.current?.focus();
            }}
          />
        )}
        {answered.isError && <p className="mb-1 px-3 text-[13px] text-[#e53935]">{answered.error.message}</p>}
        {send.isError && <p className="mb-1 px-3 text-[13px] text-[#e53935]">{send.error.message}</p>}
        {sendWarning && !send.isError && (
          <p className="mb-1 px-3 text-[13px] text-[#b26a00]" data-annot="подпись к файлу не ушла">
            {`Файл отправлен, подпись — нет: ${sendWarning}. Текст подписи вернул в поле — отправьте его сообщением.`}
          </p>
        )}
        {chat?.context?.web && chat.web_send?.ok && !send.isPending && (
          <p className="mb-1 px-3 text-[13px] text-[var(--tg-sec)]" data-annot="ответ через служебный браузер">
            {`Ответ уйдёт с номера ${webAccount(chat.context.web, channelOf(chat.channel).label)}: диалог откроется в служебном браузере, текст наберётся по знаку — несколько секунд. Только текст, до 800 знаков; ушло или нет — видно у сообщения.`}
          </p>
        )}
        {tooBig && (
          <p className="mb-1 px-3 text-[13px] text-[#e53935]">
            «{tooBig.name}» — {sizeOf(tooBig.size)}: бот отправляет файлы до 50 МБ
          </p>
        )}
        {waProblem && (
          <p className="mb-1 px-3 text-[13px] text-[#e53935]" data-annot="окно 24 ч: не отправить">
            {waProblem}
          </p>
        )}
        {note.isError && (
          <p className="mb-1 px-3 text-[13px] text-[#e53935]">{note.error.message}</p>
        )}
        {files.length > 0 && !noting && (
          <div className="mb-1.5 flex flex-wrap gap-1.5">
            {files.map((f, i) => (
              <Picked key={`${f.name}-${i}`} file={f} busy={send.isPending} onRemove={() => setFiles(files.filter((x) => x !== f))} />
            ))}
          </div>
        )}
        {replyTo && canReply && !noting && (
          <div
            className="mb-1.5 flex items-center gap-2 rounded-[14px] bg-[var(--tg-surface)] py-1.5 pl-3 pr-1.5 shadow-[var(--tg-shadow)]"
            data-annot="ответ на сообщение"
          >
            <Reply size={20} className="flex-none text-[var(--tg-primary)]" />
            <button
              type="button"
              onClick={() => jumpTo(replyTo.id)}
              title="Показать сообщение в ленте"
              className="min-w-0 flex-1 border-l-2 border-[var(--tg-primary)] pl-2 text-left text-[13px] leading-[18px]"
            >
              <div className="truncate font-medium text-[var(--tg-primary)]">
                Ответ · {replyName(replyTo.author, replyTo.out, chat && !chat.group ? titleOf(chat) : "")}
              </div>
              <div className="truncate text-[var(--tg-sec)]">{replyGist(replyTo.text, replyTo.file?.kind ?? null)}</div>
            </button>
            <button
              type="button"
              onClick={() => setReplyTo(null)}
              title="Не отвечать на это сообщение (Esc)"
              className="grid size-8 flex-none place-items-center rounded-full text-[var(--tg-sec)] hover:bg-[var(--tg-hover)]"
            >
              <X size={18} />
            </button>
          </div>
        )}
        {chat?.group && !chat.group_send ? (
          <p
            className="rounded-[18px] bg-[var(--tg-surface)] px-4 py-3 text-[14px] text-[var(--tg-sec)] shadow-[0_1px_8px_1px_rgba(0,0,0,.12)]"
            data-annot="группа только для чтения"
          >
            В этой группе нет нашего бота — отсюда её только читаем. Писать — в самой группе в{" "}
            {channelOf(chat.channel).label}.
          </p>
        ) : !noting && chat?.context?.web?.by_phone && !chat.web_send?.ok ? (
          <p
            className="rounded-[18px] bg-[var(--tg-surface)] px-4 py-3 text-[14px] text-[var(--tg-sec)] shadow-[0_1px_8px_1px_rgba(0,0,0,.12)]"
            data-annot="написать нельзя"
          >
            Переписки с клиентом нет, написать ему отсюда сейчас нельзя
            {chat.web_send?.reason ? `: ${chat.web_send.reason}` : ""}.
          </p>
        ) : !noting && chat?.context?.web && !chat.web_send?.ok ? (
          <p className="rounded-[18px] bg-[var(--tg-surface)] px-4 py-3 text-[14px] text-[var(--tg-sec)] shadow-[0_1px_8px_1px_rgba(0,0,0,.12)]">
            Переписка с номера {webAccount(chat.context.web, channelOf(chat.channel).label)}
            {!chat.context.web.account_name && " (в CRM номер без названия — sMsg_whats_phone)"} — её приносит
            расширение Chrome. Отвечать — в браузере: из кабинета в этот чат сейчас не ответить
            {chat.web_send?.reason ? ` (${chat.web_send.reason})` : ""}.{" "}
            <a
              href={webChatUrl(chat) ?? "#"}
              target="_blank"
              rel="noreferrer"
              title="Диалог откроется в вашем браузере — в том аккаунте, в который вы там вошли"
              className="inline-flex items-center gap-1 whitespace-nowrap font-medium text-[var(--tg-primary)] hover:underline"
              data-annot="открыть диалог у площадки"
            >
              <ExternalLink size={14} /> Открыть диалог в {chat.context.web.platform ?? channelOf(chat.channel).label}
            </a>
          </p>
        ) : (
          <div className="flex items-end gap-2">
            <div
              className={`flex min-h-12 min-w-0 flex-1 items-end rounded-[24px] bg-[var(--tg-surface)] py-[5px] pl-1.5 pr-4 shadow-[0_1px_8px_1px_rgba(0,0,0,.12)] ${drag ? "outline outline-2 outline-[var(--tg-primary)]" : ""}`}
            >
              <input
                ref={picker}
                type="file"
                multiple
                hidden
                onChange={(e) => {
                  addFiles(e.target.files);
                  e.target.value = "";
                }}
              />
              <button
                type="button"
                onClick={() => picker.current?.click()}
                hidden={!!chat?.context?.web || noting}
                title="Фото, видео или файл"
                className="mr-1 grid size-[38px] flex-none place-items-center rounded-full text-[var(--tg-sec)] hover:bg-[var(--tg-hover)]"
              >
                <Paperclip size={22} />
              </button>
              <button
                type="button"
                onClick={() => setCatalog(true)}
                hidden={!!chat?.group || noting}
                title="Каталог с фильтром как в Резерве: отметить платья и положить фото в ответ"
                className="mr-1 grid size-[38px] flex-none place-items-center rounded-full text-[var(--tg-sec)] hover:bg-[var(--tg-hover)]"
                data-annot="каталог платьев"
              >
                <Shirt size={22} />
              </button>
              <textarea
                ref={field}
                rows={1}
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
                    e.preventDefault();
                    submit();
                  }
                  if (e.key === "Escape" && replyTo) {
                    e.preventDefault();
                    setReplyTo(null);
                  }
                }}
                onPaste={(e) => {
                  // Скриншот из буфера — сразу во вложения (у комментария вложений нет).
                  if (e.clipboardData.files.length > 0 && !noting) {
                    e.preventDefault();
                    addFiles(e.clipboardData.files);
                  }
                }}
                placeholder={
                  mode === "comment"
                    ? `Комментарий к сделке ${chat?.deal?.id ?? ""} — клиент не увидит`
                    : mode === "salon"
                      ? `Вопрос салону по сделке ${chat?.deal?.id ?? ""}`
                      : files.length > 0
                        ? "Подпись"
                        : "Сообщение"
                }
                className="max-h-[190px] min-w-0 flex-1 resize-none bg-transparent py-2 leading-[21px] outline-none placeholder:text-[var(--tg-sec)]"
              />
            </div>
            {noting ? (
              <button
                type="button"
                onClick={submit}
                disabled={!text.trim() || note.isPending || (mode === "salon" && salonCity === null)}
                title={
                  mode === "comment"
                    ? "Записать комментарий в сделку CRM — клиенту ничего не уйдёт"
                    : salonCity === null
                      ? "Выберите город салона под полем"
                      : "Комментарий в сделку CRM и вопрос в группу салона"
                }
                aria-busy={note.isPending}
                className="grid size-12 flex-none place-items-center rounded-full bg-warn text-white disabled:opacity-50"
                data-annot="записать комментарий"
              >
                {note.isPending ? (
                  <LoaderCircle size={22} className="animate-spin motion-reduce:animate-none" />
                ) : mode === "salon" ? (
                  <Store size={22} />
                ) : (
                  <StickyNote size={22} />
                )}
              </button>
            ) : (
            <button
              type="button"
              onClick={submit}
              disabled={(!text.trim() && files.length === 0) || !!tooBig || !!waProblem || send.isPending}
              title={
                send.isPending
                  ? "Ответ отправляется — следующий можно набирать"
                  : waProblem
                    ? waProblem
                    : chat?.group
                      ? "Отправить в группу: напишет наш бот, первой строкой — ваше имя"
                      : "Отправить клиенту"
              }
              aria-busy={send.isPending}
              className={`grid size-12 flex-none place-items-center rounded-full bg-[var(--tg-primary)] text-white ${send.isPending ? "" : "disabled:opacity-50"}`}
              data-annot="отправить"
            >
              {send.isPending ? <LoaderCircle size={22} className="animate-spin motion-reduce:animate-none" /> : <SendHorizontal size={22} />}
            </button>
            )}
            {!chat?.group && !noting && (
            <>
            <TemplateButton
              onPick={(value) => {
                // Шаблон дописывается к набранному с новой строки; курсор — в поле, чтобы поправить и отправить.
                setText((had) => (had.trim() ? `${had.trimEnd()}\n${value}` : value));
                field.current?.focus();
              }}
            />
            <QuickButton
              chatId={chatId}
              channel={chat?.channel ?? ""}
              web={chat?.external_id.startsWith("web:") ?? false}
              onPick={(value) => {
                // Заготовка — как шаблон: дописывается к набранному, клиенту уйдёт по «Отправить».
                setText((had) => (had.trim() ? `${had.trimEnd()}\n${value}` : value));
                field.current?.focus();
              }}
              onFiles={addFiles}
            />
            {/* Фото чата — в группу салона города: подбор платья или сдать наряд. */}
            <SalonForwardButton chatId={chatId} />
            </>
            )}
          </div>
        )}
        {/* Кнопка AI — в ряду режимов справа (вариант B концепта 03.11.26): у поля ответа (вариант A) при
            трёх панелях на 1440 px поле сжималось до «Сообщ…» — проба на бою 03.11.26. */}
        {modeBar && !noting ? (
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">{modeBar}</div>
            <ChatAiButton ai={ai} disabled={false} />
          </div>
        ) : (
          modeBar
        )}
      </div>
      </QcComposeSwitch>
      </div>
      {qcOpen && chat?.group && <QcPanel focus={qcFocus} onClose={() => setQcOpen(false)} />}
      {groupSettings && chat?.group && canGroupEdit && (
        <aside className="fixed inset-0 z-40 flex flex-col overflow-hidden bg-bg text-fg max-xl:shadow-2xl sm:left-auto sm:w-[440px] xl:static xl:z-auto xl:w-[440px] xl:flex-none xl:rounded-[24px] xl:shadow-[0_0_4px_rgba(0,0,0,.24)]">
          <div className="flex h-11 flex-none items-center border-b border-border px-3 text-[13.5px] font-medium">
            Кто видит группу
            <button
              type="button"
              onClick={() => setGroupSettings(false)}
              title="Скрыть"
              className="ml-auto rounded p-1 text-fg-faint hover:bg-bg-hover hover:text-fg"
            >
              <X size={16} />
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto text-[13.5px]">
            <GroupAccess chatId={chatId} />
          </div>
        </aside>
      )}
      {panel && chat && !chat.group && (
        <aside className="fixed inset-0 z-40 flex flex-col overflow-hidden bg-bg text-fg max-xl:shadow-2xl sm:left-auto sm:w-[440px] xl:static xl:z-auto xl:w-[440px] xl:flex-none xl:rounded-[24px] xl:shadow-[0_0_4px_rgba(0,0,0,.24)]">
          <TabStrip
            active={side}
            label="Разделы панели"
            tail={
              <button
                type="button"
                onClick={() => setPanel(false)}
                title="Скрыть"
                className="mx-1 self-center rounded p-1 text-fg-faint hover:bg-bg-hover hover:text-fg"
              >
                <X size={16} />
              </button>
            }
          >
            {tabs.map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                onClick={() => setSide(t.id)}
                aria-selected={side === t.id}
                title={t.title}
                data-annot={`вкладка панели «${t.label}»`}
                className={`inline-flex flex-none items-center gap-1.5 whitespace-nowrap border-b-2 px-2.5 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent ${
                  side === t.id ? "border-accent font-medium text-fg" : "border-transparent text-fg-muted hover:text-fg"
                }`}
              >
                {t.label}
                {t.id === "client" && traces > 0 && (
                  <span className="rounded-full bg-[var(--tg-primary)] px-1.5 text-[12px] font-medium leading-[18px] text-white" title="Следов интереса: поиски по фото, лайки, отметки салона и каталога">
                    {traces}
                  </span>
                )}
                {t.id === "qc" && <QcTabCount />}
              </button>
            ))}
          </TabStrip>
          <div className="min-h-0 flex-1 overflow-y-auto text-[13.5px]">
            {(side === "deal" || side === "client") && (
              <div className="px-3 pt-3">
                <ChatTags chatId={chatId} tags={chat.tags ?? []} />
              </div>
            )}
            {side === "qc" && <QcList focus={qcFocus} onJump={() => window.innerWidth < 1280 && setPanel(false)} />}
            {side === "ai" && <ChatAiHistory chatId={chatId} />}
            {side === "client" && (
              <ClientTab
                chatId={chatId}
                onSearch={(chat_id, id) => setDress({ id, chat_id })}
                onCatalog={() => setCatalog(true)}
                onOffer={(_, list) => {
                  // Похожие на «понравилось»: фото с номером серии — к файлам ответа, отправляет сотрудник.
                  addFiles(list);
                  field.current?.focus();
                }}
              />
            )}
            {/* Сделка и её история — одна панель на обе вкладки; на «Клиенте» она скрыта, но жива:
                выбранная сделка, недописанный комментарий и «Время записи» вкладку переживают. */}
            <div hidden={side === "client" || side === "qc" || side === "ai"}>
              <DealPanel
                chatId={chatId}
                name={chat.name}
                deals={chat.deals}
                dealsActive={chat.deals_active ?? []}
                leads={chat.leads}
                phones={chat.phones ?? []}
                cached={{ deal: chat.deal, lead: chat.lead, phone: chat.phone, phoneChecked: chat.phone_checked }}
                view={side === "client" || side === "qc" || side === "ai" ? "deal" : side}
                onHasDeal={setHasDeal}
                onDraft={(value) => {
                  // «Время записи»: текст дописывается к набранному, курсор — в поле, чтобы поправить и отправить.
                  setText((had) => (had.trim() ? `${had.trimEnd()}\n${value}` : value));
                  field.current?.focus();
                }}
                onOffer={(value, list) => {
                  // «Фото в чат» из «Договора»: подпись с серией — к набранному, фото — к файлам ответа;
                  // похожие модели карточки серии подписи не дают — номер серии уже на фото.
                  if (value) setText((had) => (had.trim() ? `${had.trimEnd()}\n${value}` : value));
                  addFiles(list);
                  field.current?.focus();
                }}
              />
            </div>
          </div>
        </aside>
      )}
      {viewer && <Viewer file={viewer} onClose={() => setViewer(null)} />}
      {Number(params.get("serial")) > 0 && (
        <SerialCardDialog
          serial={Number(params.get("serial"))}
          onClose={() => dropSerial(setParams)}
          onOffer={(_, list) => {
            addFiles(list);
            dropSerial(setParams);
            field.current?.focus();
          }}
        />
      )}
      {catalog && (
        <DressCatalog
          chatId={chatId}
          onClose={() => setCatalog(false)}
          onOffer={(_, list) => addFiles(list)}
        />
      )}
      {dress !== null && (
        <DressSearch
          chatId={dress.chat_id}
          messageId={dress.id}
          cityHint={chat?.context?.city ?? chat?.context?.title ?? null}
          onClose={() => setDress(null)}
          onOffer={(value, list) => {
            setText((had) => (had.trim() ? `${had}\n${value}` : value));
            addFiles(list);
          }}
        />
      )}
      {forward !== null && <ForwardDialog chatId={forward.chatId} messageIds={forward.ids} onClose={() => setForward(null)} />}
    </QcProvider>
  );
}

/** Выбранный, ещё не отправленный файл: картинка — миниатюрой, остальное — именем. */
function Picked({ file, busy, onRemove }: { file: File; busy: boolean; onRemove: () => void }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    if (!file.type.startsWith("image/")) return;
    const url = URL.createObjectURL(file);
    setSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  return (
    <div className="flex max-w-[240px] items-center gap-2 rounded-[14px] bg-[var(--tg-surface)] py-1 pl-1 pr-1.5 text-[14px] shadow-[var(--tg-shadow)]">
      {src ? (
        <img src={src} alt="" className="size-10 flex-none rounded-[10px] object-cover" />
      ) : (
        <span className="grid size-10 flex-none place-items-center rounded-[10px] bg-[var(--tg-primary)] text-white">
          <FileText size={20} />
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className="block truncate">{file.name}</span>
        <span className="block text-[12px] text-[var(--tg-sec)]">{busy ? "отправляется…" : sizeOf(file.size)}</span>
      </span>
      {!busy && (
        <button type="button" onClick={onRemove} title="Убрать" className="rounded-full p-1 text-[var(--tg-sec)] hover:bg-[var(--tg-hover)]">
          <X size={16} />
        </button>
      )}
    </div>
  );
}

/** Фото во весь экран: клик мимо или Esc — закрыть. */
function Viewer({ file, onClose }: { file: ChatFile; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/90 p-2" onClick={onClose}>
      <img src={file.url ?? ""} alt={file.name} className="max-h-full max-w-full object-contain" />
      <div className="absolute right-3 top-3 flex gap-2">
        <a
          href={file.url ?? "#"}
          download={file.name}
          onClick={(e) => e.stopPropagation()}
          title="Скачать"
          className="grid size-10 place-items-center rounded-full bg-white/10 text-white hover:bg-white/20"
        >
          <Download size={20} />
        </a>
        <button type="button" title="Закрыть" className="grid size-10 place-items-center rounded-full bg-white/10 text-white hover:bg-white/20">
          <X size={20} />
        </button>
      </div>
    </div>
  );
}

/** Вложение в пузыре: фото и видео — картинкой во всю ширину пузыря, остальное — строкой с файлом. */
function Attachment({ file, out, onOpen }: { file: ChatFile; out: boolean; onOpen: (f: ChatFile) => void }) {
  const dim = out ? "text-[var(--tg-out-meta)]" : "text-[var(--tg-sec)]";
  if (file.too_big) {
    return (
      <div className="flex items-center gap-2.5 px-2.5 pt-2">
        <span className="grid size-11 flex-none place-items-center rounded-full bg-[var(--tg-primary)] text-white">
          <FileText size={22} />
        </span>
        <span className="min-w-0">
          <span className="block truncate font-medium">{file.name}</span>
          <span className={`block text-[13px] ${dim}`}>
            {KIND[file.kind]} · {sizeOf(file.size)} · больше 20 МБ — открыть можно только в Telegram
          </span>
        </span>
      </div>
    );
  }
  // Товар площадки (магазин ВК): по нему клиент пишет — фото, название, цена, переход к товару.
  if (file.kind === "product") {
    return (
      <a
        href={file.link ?? "#"}
        target="_blank"
        rel="noreferrer"
        className="flex items-start gap-2.5 px-2.5 pt-2"
        title="Открыть товар у площадки"
        data-annot="товар из сообщения"
      >
        {file.thumb_url && (
          <img src={file.thumb_url} alt={file.name} loading="lazy" className="h-[120px] w-[100px] flex-none rounded-lg bg-black/20 object-cover" />
        )}
        <span className="min-w-0">
          <span className={`block text-[13px] ${dim}`}>Товар</span>
          <span className="block break-words font-medium leading-[19px]">{file.name}</span>
          {file.price && <span className="block font-medium">{file.price}</span>}
          {file.sku && <span className={`block text-[13px] ${dim}`}>арт. {file.sku}</span>}
          <span className={`mt-0.5 flex items-center gap-1 text-[13px] ${dim}`}>
            <ExternalLink size={14} /> открыть у площадки
          </span>
        </span>
      </a>
    );
  }
  // Площадка файлом не отдаёт (видео ВК): кадр и переход к ней.
  if (!file.url) {
    return (
      <a href={file.link ?? "#"} target="_blank" rel="noreferrer" className="relative block" title="Открыть у площадки">
        {file.thumb_url ? (
          <img src={file.thumb_url} alt={file.name} loading="lazy" className="max-h-[420px] w-full bg-black/20 object-cover" />
        ) : (
          <div className="px-2.5 pt-2 font-medium">{KIND[file.kind]}: {file.name}</div>
        )}
        <span className="absolute bottom-2 left-2 flex items-center gap-1 rounded-full bg-black/60 px-2 py-0.5 text-[13px] text-white">
          <ExternalLink size={14} /> {KIND[file.kind]} — открыть у площадки
        </span>
      </a>
    );
  }
  const url = file.url;
  // Место под картинку держим по её размерам: лента не прыгает, пока файл едет.
  const ratio = file.width && file.height ? { aspectRatio: `${file.width} / ${file.height}` } : undefined;
  if (file.kind === "photo" || file.kind === "sticker") {
    return (
      <button type="button" onClick={() => onOpen(file)} className="block w-full" title="Открыть">
        <img
          src={file.thumb_url ?? url}
          alt={file.name}
          loading="lazy"
          style={ratio}
          className="max-h-[420px] w-full rounded-[inherit] bg-black/20 object-cover"
        />
      </button>
    );
  }
  if (file.kind === "video" || file.kind === "animation" || file.kind === "video_note") {
    return (
      <video
        src={url}
        poster={file.thumb_url ?? undefined}
        controls={file.kind !== "animation"}
        autoPlay={file.kind === "animation"}
        loop={file.kind === "animation"}
        muted={file.kind === "animation"}
        playsInline
        preload={file.thumb_url ? "none" : "metadata"}
        style={ratio}
        className="max-h-[420px] w-full rounded-[inherit] bg-black"
      />
    );
  }
  if (file.kind === "voice" || file.kind === "audio") {
    return (
      <div className="px-2 pt-2">
        {file.kind === "audio" && <div className="truncate px-1 pb-1 font-medium">{file.name}</div>}
        <audio src={url} controls preload="none" className="h-10 w-[260px] max-w-full" />
        {/* Текст голосового (GigaAM, фон раз в минуту): читать быстрее, чем слушать. */}
        {file.transcript && (
          <div className="max-w-[340px] px-1 pt-1.5 leading-[20px]" title="Расшифровка голосового" data-annot="расшифровка голосового">
            {file.transcript}
          </div>
        )}
      </div>
    );
  }
  return (
    <a href={url} target="_blank" rel="noreferrer" download={file.name} className="flex items-center gap-2.5 px-2.5 pt-2" title="Открыть файл">
      <span className={`grid size-11 flex-none place-items-center rounded-full ${out ? "bg-black/15" : "bg-[var(--tg-primary)] text-white"}`}>
        <FileText size={22} />
      </span>
      <span className="min-w-0">
        <span className="block truncate font-medium">{file.name}</span>
        <span className={`block text-[13px] ${dim}`}>{sizeOf(file.size)}</span>
      </span>
    </a>
  );
}

/** Галочки WhatsApp: одна — отправлено, две — доставлено, синие — прочитано, крест — не дошло. */
function Ticks({ status }: { status: string | null | undefined }) {
  if (status === "sending") return <Clock size={13} />;
  if (status === "error") return <X size={14} className="text-[#e53935]" />;
  if (status === "read") return <CheckCheck size={14} className="text-[#53bdeb]" />;
  if (status === "delivered") return <CheckCheck size={14} />;
  return <Check size={14} />;
}
const STATUS: Record<string, string> = { sent: "Отправлено", delivered: "Доставлено", read: "Прочитано" };
const metaTitle = (m: ChatMessage) =>
  [
    m.out && m.author ? `Отправил: ${m.author}` : "",
    m.out && m.auto ? "Отправлено автоматически — ответом сотрудника не считается" : "",
    m.status === "sending"
      ? "Отправляется — клиент его ещё не получил"
      : m.status === "error"
        ? `Не доставлено: ${m.error ?? "ошибка"}`
        : (m.status && STATUS[m.status]) ?? "",
  ]
    .filter(Boolean)
    .join(" · ") || undefined;

/** Что за ссылкой записи: цель кнопки достигнута (клиент записан) — зелёная отметка; открыл, но
 * не записался — «нажали». Открытия — фактом, не числом; переносы — числом. */
function ZapisMark({ z, out }: { z: NonNullable<ChatButton["zapis"]>; out: boolean }) {
  const pill = "inline-flex items-center gap-1 rounded-full px-1.5 py-px";
  const goal = z.at ? (
    <span className={`${pill} bg-[#2e9e4f] text-white`} title="Клиент записан — цель кнопки достигнута">
      <Check size={12} strokeWidth={3} />
      записан на {Number(z.at.slice(8, 10))} {MONTHS[Number(z.at.slice(5, 7)) - 1]} {hhmm(z.at)}
    </span>
  ) : z.books + z.moves ? (
    <span
      className={`${pill} border border-[#2e9e4f] text-[#2e9e4f]`}
      title="По этой ссылке клиент записывался, но сейчас сделка не в «Ждем клиента»"
    >
      <Check size={12} strokeWidth={3} />
      записывался, сейчас записи нет
    </span>
  ) : z.opens ? (
    <span className={`${pill} bg-black/10`} title="Кнопку нажали: страницу записи открывали, время не выбрали">
      <Eye size={12} />
      открыли, не записались
    </span>
  ) : (
    <span>не открывали</span>
  );
  return (
    <span
      className={`flex flex-wrap items-center justify-center gap-x-1.5 gap-y-0.5 text-[12px] font-normal leading-[16px] ${out ? "text-[var(--tg-out-meta)]" : "text-[var(--tg-sec)]"}`}
    >
      {goal}
      {!!z.moves && <span>переносов: {z.moves}</span>}
    </span>
  );
}

/**
 * Ответ уходит: что происходит прямо сейчас. Через служебный браузер ответ набирается несколько
 * секунд — показываем шаг словами и полосу из пяти делений (шаги — `chat/web.ШАГИ` на сервере);
 * у остальных каналов шага нет, отправка идёт меньше секунды.
 */
const SEND_STEPS = [
  { codes: ["tunnel", "tab"], label: "подключаюсь к служебному браузеру" },
  { codes: ["dialog"], label: "открываю диалог с клиентом" },
  { codes: ["field", "type"], label: "печатаю текст в поле" },
  { codes: ["click", "wait"], label: "нажал «Отправить», жду сообщение в переписке" },
  { codes: ["tick"], label: "сообщение в переписке, жду галку" },
];
function Sending({ step }: { step: string | null | undefined }) {
  const at = step ? SEND_STEPS.findIndex((s) => s.codes.includes(step)) : -1;
  const now = SEND_STEPS[at];
  return (
    <div
      role="status"
      className="clear-both mt-1.5 border-t border-black/10 pt-1.5 text-[13px] leading-[18px] text-[var(--tg-out-meta)]"
      data-annot="ход отправки"
    >
      <div className="flex items-center gap-1.5">
        <LoaderCircle size={14} className="flex-none animate-spin motion-reduce:animate-none" aria-hidden />
        <span>
          <span className="font-medium">Отправляется</span>
          {now ? ` · ${now.label}…` : "…"}
        </span>
      </div>
      {now && (
        <div className="mt-1.5 flex gap-1" aria-hidden>
          {SEND_STEPS.map((s, i) => (
            <span
              key={s.label}
              title={s.label}
              className={`h-1 flex-1 rounded-full bg-current ${i < at ? "opacity-70" : i === at ? "animate-pulse opacity-70 motion-reduce:animate-none" : "opacity-20"}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function Service({ children }: { children: ReactNode }) {
  return (
    <div className="my-1 flex-none self-center rounded-full bg-[var(--tg-service)] px-2.5 py-0.5 text-[15px] font-medium text-white">
      {children}
    </div>
  );
}

/** Короткая подпись файла в цитате ответа, когда текста нет. */
const FILE_GIST: Record<string, string> = {
  photo: "Фото",
  video: "Видео",
  animation: "GIF",
  video_note: "Видеосообщение",
  voice: "Голосовое",
  audio: "Аудио",
  sticker: "Стикер",
  document: "Файл",
};
const replyGist = (text: string, kind: string | null) =>
  text.trim() || (kind ? (FILE_GIST[kind] ?? "Файл") : "Сообщение");
/** Чьё сообщение в цитате: автор как в ленте; у клиента без имени — название чата. */
const replyName = (author: string, out: boolean | null, client: string) =>
  author || (out ? "Мы" : client || "Клиент");
/** К исходному сообщению ответа: прокрутить и коротко подсветить, как Telegram. */
function jumpTo(id: number | null, behavior: ScrollBehavior = "smooth") {
  const el = id ? document.getElementById(`msg-${id}`) : null;
  if (!el) return;
  el.scrollIntoView({ block: "center", behavior });
  el.animate([{ backgroundColor: "rgba(51,144,236,.28)" }, { backgroundColor: "rgba(51,144,236,0)" }], {
    duration: 1600,
    easing: "ease-out",
  });
}

function Bubble({
  m,
  prev,
  next,
  newDay,
  from,
  group,
  onOpen,
  onDress,
  onForward,
  onReply,
}: {
  m: ChatMessage;
  prev: ChatMessage | undefined;
  next: ChatMessage | undefined;
  /** Первая запись ленты за день (считает `merge` по всем записям — и звонкам, и комментариям): над ней — дата. */
  newDay: boolean;
  /** Общая лента клиента: чат этого сообщения — над первым сообщением подряд из него пишем, где общались. */
  from: ChatBrief | undefined;
  /** Группа сотрудников: слева пишут разные люди — имя над первым сообщением каждого подряд. */
  group: boolean;
  onOpen: (f: ChatFile) => void;
  onDress: (m: ChatMessage) => void;
  /** Переслать это сообщение в чат салона. */
  onForward: (m: ChatMessage) => void;
  /** Ответить на это сообщение (реплай); нет — в этом канале или чате нельзя. */
  onReply: ((m: ChatMessage) => void) | undefined;
}) {
  const { search } = useLocation();
  const [was, setWas] = useState(false);
  const place = from && (newDay || prev?.chat_id !== m.chat_id) ? from : null;
  // Хвостик — у последнего пузыря подряд от одной стороны (и из одного чата).
  const tail =
    !next ||
    next.out !== m.out ||
    next.chat_id !== m.chat_id ||
    next.date.slice(0, 10) !== m.date.slice(0, 10) ||
    (group && !m.out && next.author !== m.author);
  const who = group && !m.out && !!m.author && (newDay || !prev || prev.out || prev.author !== m.author);
  const shape = m.out
    ? `bg-[var(--tg-out)] text-[var(--tg-out-text)] ${tail ? "rounded-[15px_15px_0_15px]" : "rounded-[15px_15px_6px_15px]"}`
    : `bg-[var(--tg-surface)] ${tail ? "rounded-[15px_15px_15px_0]" : "rounded-[15px_15px_15px_6px]"}`;
  const canForward = m.id > 0 && m.status !== "sending";
  const canReply = !!onReply && canForward;
  // Меню «⋯»: те же действия списком + замечание контроля качества (с правом `qc_remark`).
  const qcSel = useQc();
  const menu: MenuItem[] = canForward && !qcSel?.active
    ? [
        ...(canReply
          ? [{ label: "Ответить", icon: <Reply size={17} />, hint: "Клиент увидит цитату (двойной клик по сообщению — то же)", onClick: () => onReply(m), annot: "меню: ответить" }]
          : []),
        { label: "Переслать в салон", icon: <Forward size={17} />, hint: "Текст и фото с номером клиента и отметкой стилиста примерки", onClick: () => onForward(m), annot: "меню: переслать в салон" },
        ...(qcSel?.pickable && !group
          ? [{ label: "Выбрать несколько", icon: <ListChecks size={17} />, hint: "Отметить ещё сообщения и переслать в салон пачкой", onClick: () => qcSel.pick({ kind: "message" as const, id: m.id, chat_id: m.chat_id }), annot: "меню: выбрать несколько" }]
          : []),
        ...(qcSel?.add ? [remarkItem(() => qcSel.start({ kind: "message", id: m.id, chat_id: m.chat_id }))] : []),
      ]
    : [];
  // Место под кнопки сбоку: «переслать» 40 px, «ответить» и «⋯» — по 44 px.
  const extra = canForward ? 40 + (canReply ? 44 : 0) + (menu.length ? 44 : 0) : 0;
  // Кнопки сбоку от пузыря — как служебные элементы Telegram поверх обоев (круг цвета даты «Сегодня»,
  // белый значок): зона клика 36 px, видимый круг 28 px — место под кнопку то же, лента не прыгает.
  const sideBtn = "group/side grid size-9 cursor-pointer place-items-center rounded-full";
  const sideIcon =
    "grid size-7 place-items-center rounded-full bg-[var(--tg-service)] text-white transition-[background-color,transform] duration-150 group-hover/side:bg-[var(--tg-primary)] group-active/side:scale-90";
  // Фото и видео занимают пузырь целиком, поэтому ширина у него своя.
  const visual = !!m.file && !m.file.too_big && ["photo", "sticker", "video", "animation", "video_note"].includes(m.file.kind);
  return (
    <>
      {newDay && <Service>{dayLabel(m.date)}</Service>}
      {place && (
        <Link
          to={{ pathname: `/chats/${place.id}`, search }}
          title={`${placeFull(place)} · ${titleOf(place)} — открыть этот чат`}
          className="my-0.5 flex flex-none items-center gap-1.5 self-center rounded-full bg-[var(--tg-surface)] px-2.5 py-0.5 text-[12px] font-medium text-[var(--tg-sec)] shadow-[var(--tg-shadow)] hover:text-[var(--tg-text)]"
          data-annot="канал сообщений в общей ленте"
        >
          <span className="size-2 flex-none rounded-full" style={{ background: channelOf(place.channel).color }} />
          {placeOf(place)}
        </Link>
      )}
      {/* Пузырь и сбоку от него — кнопка пересылки в салон: у клиента справа, у нашего сообщения слева. */}
      <div
        id={m.id > 0 ? `msg-${m.id}` : undefined}
        className={`group/msg flex flex-none items-end gap-1.5 rounded-[15px] ${m.out ? "flex-row-reverse self-end" : "self-start"} ${tail ? "mb-1" : ""}`}
        style={
          visual
            ? { width: `min(calc(85% + ${extra}px), 100%, ${420 + extra}px)` }
            : { maxWidth: `min(calc(85% + ${extra}px), 100%, ${480 + extra}px)` }
        }
      >
      <div
        className={`min-w-0 overflow-hidden shadow-[var(--tg-shadow)] ${visual ? "flex-1" : ""} ${shape}`}
        aria-busy={m.status === "sending" || undefined}
        onDoubleClick={
          canReply
            ? () => {
                // Как в Telegram Desktop: двойной клик — ответить; выделенное слово не оставляем.
                window.getSelection()?.removeAllRanges();
                onReply(m);
              }
            : undefined
        }
      >
        {who && (
          <div className="truncate px-2.5 pt-1.5 text-[13px] font-medium leading-[18px] text-[var(--tg-primary)]" data-annot="автор в группе">
            {m.author}
          </div>
        )}
        {m.reply && (
          <button
            type="button"
            onClick={() => jumpTo(m.reply!.id)}
            disabled={!m.reply.id}
            title={m.reply.id ? "Показать сообщение, на которое ответили" : "Исходного сообщения нет в нашей копии переписки"}
            className={`mx-1.5 mt-1.5 block w-[calc(100%-12px)] min-w-[120px] rounded-[6px] border-l-[3px] px-2 py-0.5 text-left text-[13px] leading-[18px] ${
              m.out ? "border-[var(--tg-out-meta)] bg-black/5 hover:bg-black/10" : "border-[var(--tg-primary)] bg-[var(--tg-primary)]/10 hover:bg-[var(--tg-primary)]/15"
            }`}
            data-annot="цитата ответа"
          >
            <span className={`block truncate font-medium ${m.out ? "text-[var(--tg-out-meta)]" : "text-[var(--tg-primary)]"}`}>
              {m.reply.id ? replyName(m.reply.author, m.reply.out, from ? titleOf(from) : "") : "Ответ на сообщение"}
            </span>
            <span className="block truncate">{replyGist(m.reply.text, m.reply.file_kind)}</span>
          </button>
        )}
        {m.file && <Attachment file={m.file} out={m.out} onOpen={onOpen} />}
        {/* Фото клиента или товар магазина ВК, по которому он пишет, — фото товара. */}
        {!m.out && ((m.file?.kind === "photo" && m.file.url && !m.file.too_big) || (m.file?.kind === "product" && m.file.thumb_url)) && (
          <button
            type="button"
            onClick={() => onDress(m)}
            className="flex w-full items-center justify-center gap-1.5 border-b border-black/5 px-2.5 py-1.5 text-[13px] font-medium text-[var(--tg-primary)] hover:bg-[var(--tg-hover)]"
            title="Похожие платья нашего каталога по этому фото"
            data-annot="найти похожие платья"
          >
            <ScanSearch size={15} /> Найти похожие
          </button>
        )}
        <div className="flow-root px-2.5 py-1.5">
        {m.mailing && (
          <div
            className={`mb-0.5 flex items-center gap-1 text-[12px] font-medium leading-[16px] ${m.out ? "text-[var(--tg-out-meta)]" : "text-[var(--tg-primary)]"}`}
            title={m.mailing.press ? "Клиент нажал кнопку под сообщением рассылки" : "Сообщение ушло рассылкой, а не от сотрудника"}
            data-annot="рассылка"
          >
            <Megaphone size={12} className="flex-none" />
            <span className="truncate">
              {m.mailing.press ? `Кнопка рассылки «${m.mailing.title}»` : `Рассылка «${m.mailing.title}»`}
            </span>
          </div>
        )}
        <span className="whitespace-pre-wrap break-words leading-[21px]">
          {m.text ? <MessageText text={m.text} out={m.out} /> : m.file ? "" : "‹без текста›"}
        </span>
        <span
          title={metaTitle(m)}
          className={`float-right ml-3 mt-[7px] flex items-center gap-1 text-[12px] leading-none ${m.out ? "text-[var(--tg-out-meta)]" : "text-[var(--tg-sec)]"}`}
        >
          {m.out && m.author && <span className="max-w-[140px] truncate">{m.author.split(" ")[0]}</span>}
          {m.out && m.auto && <span>авто</span>}
          {!!m.edits?.length && (
            <button
              type="button"
              onClick={() => setWas((v) => !v)}
              title={was ? "Скрыть, что было" : "Сообщение исправили после отправки — показать, что было"}
              className="underline decoration-dotted underline-offset-2"
              data-annot="сообщение исправлено"
            >
              изменено
            </button>
          )}
          {hhmm(m.date)}
          {m.out && <Ticks status={m.status} />}
        </span>
        {m.out && m.status === "sending" && <Sending step={m.step} />}
        {m.out && m.status === "error" && m.id > 0 && <WaError m={m} />}
        {m.out && m.status === "error" && m.id < 0 && (
          <div className="clear-both mt-1.5 border-t border-black/10 pt-1.5 text-[13px] font-medium leading-[18px] text-[#e53935]" data-annot="исход ответа через браузер">
            {m.error ?? "не ушло"}
          </div>
        )}
        {was && !!m.edits?.length && (
          <div
            className={`clear-both mt-1.5 border-t border-black/10 pt-1.5 text-[13px] leading-[18px] ${m.out ? "text-[var(--tg-out-meta)]" : "text-[var(--tg-sec)]"}`}
            data-annot="что было до правки"
          >
            {m.edits.map((e) => (
              <div key={e.date} className="whitespace-pre-wrap break-words">
                <span className="text-[12px]">
                  было до {e.date.slice(0, 10) === m.date.slice(0, 10) ? "" : `${dayLabel(e.date)} `}
                  {hhmm(e.date)}:{" "}
                </span>
                {e.text || "‹без текста›"}
              </div>
            ))}
          </div>
        )}
        </div>
        {!!m.buttons?.length && (
          <div className="flex flex-col border-t border-black/10" data-annot="кнопки шаблона">
            {m.buttons.map((b, i) => {
              // Кнопки с одним адресом (обе у шаблона новому лиду) ведут на одну страницу: что
              // с ней сделал клиент, пишем у первой — какую из них нажали, узнать нельзя.
              const first = m.buttons!.findIndex((x) => x.url === b.url) === i;
              const done = !!b.zapis && (!!b.zapis.at || b.zapis.books + b.zapis.moves > 0);
              return (
                <a
                  key={i}
                  href={b.url}
                  target="_blank"
                  // Страница записи узнаёт кабинет по Referer и заход сотрудника открытием не считает.
                  rel={b.kind === "zapis" ? "noopener" : "noreferrer"}
                  title={b.url}
                  className={`flex flex-col items-center gap-0.5 border-b border-black/5 px-2.5 py-1.5 text-[13px] font-medium last:border-b-0 ${done ? "bg-[#2e9e4f]/25 hover:bg-[#2e9e4f]/35" : "hover:bg-black/5"}`}
                >
                  <span className="flex max-w-full items-center justify-center gap-1.5">
                    <ExternalLink size={14} className="flex-none" />
                    <span className="truncate">{b.label}</span>
                  </span>
                  {b.zapis &&
                    (first ? (
                      <ZapisMark z={b.zapis} out={m.out} />
                    ) : (
                      <span
                        className={`text-center text-[12px] font-normal leading-[16px] ${m.out ? "text-[var(--tg-out-meta)]" : "text-[var(--tg-sec)]"}`}
                      >
                        та же ссылка
                      </span>
                    ))}
                </a>
              );
            })}
          </div>
        )}
        {!!m.mailing?.buttons?.length && <MailingButtons mark={m.mailing} sent={m.date} out={m.out} />}
      </div>
      {canForward && (
        // Сбоку от пузыря: «переслать в салон» — всегда, «ответить» — как в Telegram, при наведении
        // (на сенсорном экране наведения нет — там видна всегда). Место под неё держим, чтобы
        // лента не прыгала.
        <div className={`mb-0.5 flex flex-none gap-1.5 ${m.out ? "flex-row-reverse" : ""}`}>
          <button
            type="button"
            onClick={() => onForward(m)}
            title="Переслать в чат салона: текст и фото с номером клиента и отметкой стилиста примерки"
            className={sideBtn}
            data-annot="переслать в салон"
          >
            <span className={sideIcon}>
              <Forward size={15} />
            </span>
          </button>
          {canReply && (
            <button
              type="button"
              onClick={() => onReply(m)}
              title="Ответить на это сообщение — клиент увидит цитату (двойной клик по сообщению — то же)"
              className={`${sideBtn} transition-opacity focus-visible:opacity-100 group-hover/msg:opacity-100 [@media(hover:hover)]:opacity-0`}
              data-annot="ответить на сообщение"
            >
              <span className={sideIcon}>
                <Reply size={15} />
              </span>
            </button>
          )}
          {menu.length > 0 && (
            <span className="transition-opacity focus-within:opacity-100 group-hover/msg:opacity-100 [@media(hover:hover)]:opacity-0">
              <MessageMenu items={menu} className={sideBtn}>
                <span className={sideIcon}>
                  <MoreHorizontal size={15} />
                </span>
              </MessageMenu>
            </span>
          )}
        </div>
      )}
      </div>
    </>
  );
}

/**
 * Кнопки-ответы рассылки («Хорошо» / «Плохо», «Подтвердить примерку» / «Отменить примерку»):
 * нажатая — зелёная, с временем нажатия; дальше по цепочке (переспрос отмены) — строкой «затем»;
 * ни одной — «клиент кнопку не нажал».
 */
function MailingButtons({ mark, sent, out }: { mark: ChatMailingMark; sent: string; out: boolean }) {
  const meta = `text-[12px] font-normal leading-[16px] ${out ? "text-[var(--tg-out-meta)]" : "text-[var(--tg-sec)]"}`;
  const when = (at: string) => `${at.slice(0, 10) === sent.slice(0, 10) ? "" : `${dayLabel(at)} `}${hhmm(at)}`;
  const buttons = mark.buttons ?? [];
  const then = mark.then ?? [];
  return (
    <div className="flex flex-col border-t border-black/10" data-annot="кнопки рассылки">
      {buttons.map((b) => (
        <div
          key={b.step}
          className={`flex flex-col items-center gap-0.5 border-b border-black/5 px-2.5 py-1.5 text-[13px] font-medium last:border-b-0 ${b.at ? "bg-[#2e9e4f]/25" : ""}`}
        >
          <span className={`flex max-w-full items-center justify-center gap-1.5 ${b.at ? "" : "opacity-60"}`}>
            {b.at && <Check size={14} className="flex-none" />}
            <span className="truncate">{b.label}</span>
          </span>
          {b.at && <span className={meta}>клиент нажал · {when(b.at)}</span>}
        </div>
      ))}
      {then.map((b) => (
        <div key={b.step} className={`border-b border-black/5 px-2.5 py-1 text-center last:border-b-0 ${meta}`}>
          затем «{b.label}»{b.at ? ` · ${when(b.at)}` : ""}
        </div>
      ))}
      {![...buttons, ...then].some((b) => b.at) && (
        <div className={`px-2.5 py-1 text-center ${meta}`}>клиент кнопку не нажал</div>
      )}
    </div>
  );
}
